import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { LayoutDashboard, Megaphone, Users, Compass, GitBranch, Mail, Layers, Link2, MessageSquare, Calendar, BarChart2, Flame, Users2, CreditCard, Settings as SettingsIcon, LogOut, FileText, CheckCircle2, XCircle, Info, X, ChevronsLeft, ChevronsRight } from "lucide-react";
import AppLogo from "./AppLogo";
import wordmarkLockup from "./sr-wordmark-lockup.png";
import { C, FONT } from "./ui/theme";
import { Modal, ModalBody, ModalTitle, ModalDescription, ModalActions, ModalAction } from "./ui/Modal";
import { Tooltip } from "./ui/Tooltip";
import LandingPage from "./LandingPage";
import LoginPage from "./LoginPage";
import TrialSignupPage from "./TrialSignupPage";
import OnboardingFlow from "./OnboardingFlow";
import PlanSelectionPage from "./PlanSelectionPage";
import AcceptInvitePage from "./AcceptInvitePage";
import * as API from "./api";
import EmailWarmup from "./EmailWarmup";
import ConnectedAccounts from "./ConnectedAccounts";
import ApiKeysSettings from "./ApiKeysSettings";
import ContactsPage from "./ContactsPage";
import TemplatesPage from "./TemplatesPage";
import OutlookWorkspace from "./OutlookWorkspace";
import CampaignsPage from "./CampaignsPage";
import TeamPage from "./TeamPage";
import SequenceBuilder from "./SequenceBuilder";
import MailMerge from "./MailMerge";
import CalendarPage from "./CalendarPage";
import DomainChecker from "./DomainChecker";
import { TermsOfService, PrivacyPolicy } from "./LegalPages";
import { TimezoneProvider, TimezoneSelector, useTz } from "./TimezoneContext";
// Phase 3 (core-pages token migration, see linear-brewing-toast.md): the
// app used to define its own local Fluent/Azure-blue palette here (`const T
// = {...}`, accent:"#0078D4") completely separate from ui/theme.js's Apple
// palette `C` — every page below read from this local T instead. Phase 1
// deliberately gave `C` a same-named counterpart for every key T had (see
// ui/theme.js's "Phase 1 design-system foundation" section) specifically so
// this migration could be a pure token-name swap with zero value
// remapping. Every `T.xxx` reference in this file has been renamed to
// `C.xxx`; this object is now unreferenced and removed.

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,400;0,500;1,400&family=Instrument+Serif:ital@0;1&family=Geist:wght@300;400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  html,body{height:100%;background:${C.bg}}
  body{color:${C.text};font-family:'Geist',sans-serif;font-size:13px;line-height:1.6;overflow-x:hidden}
  ::-webkit-scrollbar{width:3px;height:3px}
  ::-webkit-scrollbar-track{background:transparent}
  ::-webkit-scrollbar-thumb{background:${C.border};border-radius:2px}
  input,select,textarea{background:${C.bgCard};border:1px solid ${C.border};color:${C.text};border-radius:8px;padding:8px 12px;font-family:'Geist',sans-serif;font-size:13px;outline:none;transition:border-color .15s,box-shadow .15s;width:100%}
  input:focus,select:focus,textarea:focus{border-color:${C.accent};box-shadow:0 0 0 3px ${C.accent}18}
  input::placeholder,textarea::placeholder{color:${C.textSub}}
  button{cursor:pointer;font-family:'Geist',sans-serif;font-size:13px;border:none;transition:all .15s;outline:none}
  @keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
  /* App-wide page-navigation transition — Cross-fade + scale, applied by
     <PageTransitionHost> around every page (not any one page's own
     markup), so no individual page's JSX/behavior changes. This is the
     same style macOS System Settings uses between its own sidebar panes:
     a soft opacity fade paired with a very subtle scale (98.5% <-> 100%),
     no sliding, no warping. Deliberately understated — there's no shape
     math that can go visibly wrong, and it reads as premium precisely
     because there's nothing to notice failing. Pure opacity/transform —
     never touches layout (width/height/flow), so scroll position and
     every page's own logic are unaffected. */
  @keyframes pageFadeScaleIn{
    0%{opacity:0;transform:scale(.985);}
    100%{opacity:1;transform:scale(1);}
  }
  @keyframes pageFadeScaleOut{
    0%{opacity:1;transform:scale(1);}
    100%{opacity:0;transform:scale(.985);}
  }
  /* fill-mode was "both" on both of these, which — via the "forwards" half
     of "both" — leaves transform:scale(1)/scale(.985) actually APPLIED on
     this wrapper div forever after the animation finishes (not just during
     it), even though scale(1) looks like a no-op. Per the CSS spec, ANY
     non-none transform on an ancestor — even an identity one — makes that
     ancestor the containing block for every position:fixed descendant
     instead of the viewport. Every one of this app's full-screen dialogs
     (Templates' editor, Sequence Builder's, Mail Merge's, the shared
     ui/Modal, etc.) renders as position:fixed *inside* this wrapper, which
     itself sits inside <main>'s scrollable, height-clamped panel — so once
     the page-in animation finished, those dialogs were silently being
     positioned/clipped against <main>'s scrolled, padded box instead of
     the real viewport. That's what produced "the dialog opens with its
     title bar (drag/minimize/maximize/close) cut off above the visible
     area, and there's no way to scroll or drag it back into view" — the
     title bar wasn't gone, it was clipped by <main>'s own overflow. This
     also explains the same symptom in the desktop app: the Electron shell
     just loads this same web app from app.sdrreachout.com, so once this
     fix ships to production, the desktop app picks it up automatically —
     no separate desktop-side change needed for this particular bug.
     Dropping "both" to plain (fill-mode: none) removes the transform the
     instant each animation ends, so these wrappers stop being fixed's
     containing block again — visually identical (100%/0% keyframe values
     are unchanged), just no longer leaking a stale transform. */
  .page-fadescale-in{animation:pageFadeScaleIn .22s cubic-bezier(.25,.1,.25,1);}
  /* -out keeps "forwards" (unlike -in above): it's unmounted via a 240ms
     timeout in PageTransitionHost, ~80ms after this .16s animation
     finishes, so without "forwards" it would revert to opacity:1/no-
     transform for that last stretch and visibly flash back before
     disappearing. Its containing-block leak is bounded to that same
     ~240ms window (it's gone from the DOM after), unlike -in's, which
     persisted for as long as the page stayed mounted — so it's left as
     is rather than risking the flash. */
  .page-fadescale-out{animation:pageFadeScaleOut .16s cubic-bezier(.4,0,1,1) forwards;pointer-events:none;}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}
  @keyframes slideIn{from{transform:translateX(10px);opacity:0}to{transform:translateX(0);opacity:1}}
  .fade-up{animation:fadeUp .25s ease both}
  .spin{animation:spin .7s linear infinite}
  .pulse{animation:pulse 2s ease infinite}
  .slide-in{animation:slideIn .2s ease both}

  /* Mobile responsiveness — this app had zero responsive rules before */
  @media (max-width: 768px) {
    .app-main{margin-left:0!important;max-width:100vw!important;padding:16px!important;}
    .responsive-grid{grid-template-columns:1fr!important;}
    .hide-on-mobile{display:none!important;}
    .mobile-hamburger{display:flex!important;}
    /* comfortable edges instead of content butting against the screen border */
    .fade-up{padding-left:2px;padding-right:2px;}
  }
  @media (min-width: 769px) {
    .mobile-hamburger{display:none!important;}
  }
`;

//  HELPERS
const mono  = {fontFamily:"'DM Mono',monospace"};

function Spinner({size=13,color=C.accent}){
  return <div className="spin" style={{width:size,height:size,border:`2px solid ${C.border}`,borderTopColor:color,borderRadius:"50%",display:"inline-block",flexShrink:0}}/>;
}
function Badge({children,bg,tc,dot,sm}){
  return(
    <span style={{background:bg,color:tc,fontSize:sm?10:11,padding:sm?"2px 7px":"3px 9px",borderRadius:99,fontWeight:500,whiteSpace:"nowrap",...mono,display:"inline-flex",alignItems:"center",gap:4}}>
      {dot&&<span style={{width:5,height:5,borderRadius:"50%",background:tc,flexShrink:0}}/>}{children}
    </span>
  );
}
function Btn({children,primary,ghost,danger,sm,full,onClick,disabled,style={}}){
  const base={border:"none",borderRadius:8,padding:sm?"5px 12px":"8px 16px",fontWeight:500,fontSize:sm?12:13,display:"inline-flex",alignItems:"center",gap:6,cursor:disabled?"not-allowed":"pointer",opacity:disabled?.55:1,whiteSpace:"nowrap",...style};
  if(full){ base.width="100%"; base.justifyContent="center"; }
  if(primary)return<button style={{...base,background:disabled?C.accentMuted:C.accent,color:disabled?C.textSub:"#fff"}}onClick={!disabled?onClick:undefined}>{children}</button>;
  if(danger)return<button style={{...base,background:C.redMuted,color:C.red,border:`1px solid ${C.red}30`}}onClick={!disabled?onClick:undefined}>{children}</button>;
  if(ghost)return<button style={{...base,background:"transparent",color:C.textSub,border:"none"}}onClick={!disabled?onClick:undefined}>{children}</button>;
  return<button style={{...base,background:"transparent",color:C.textSub,border:`1px solid ${C.border}`}}onClick={!disabled?onClick:undefined}>{children}</button>;
}
function Card({children,style={},accent,onClick}){
  return<div style={{background:C.bgCard,border:`1px solid ${accent?C.accent+"55":C.border}`,borderRadius:12,padding:"18px 20px",cursor:onClick?"pointer":"default",boxShadow:accent?"0 1px 3px rgba(0,120,212,.08), 0 1px 2px rgba(16,24,40,.04)":"0 1px 2px rgba(16,24,40,.04), 0 1px 1px rgba(16,24,40,.03)",...style}}onClick={onClick}>{children}</div>;
}
function Label({children}){return<p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".6px",marginBottom:5,fontWeight:500}}>{children}</p>;}
function Toggle({on,onChange}){
  return<div onClick={()=>onChange(!on)} style={{width:34,height:18,borderRadius:9,background:on?C.accent:C.border,cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}><div style={{width:12,height:12,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:on?19:3,transition:"left .2s",boxShadow:"0 1px 3px #0006"}}/></div>;
}
function Avatar({init,size=32,color=C.accent}){
  return<div style={{width:size,height:size,borderRadius:"50%",background:color+"1A",border:`1px solid ${color}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:Math.round(size*.35),fontWeight:500,color,flexShrink:0,...mono}}>{init}</div>;
}
function ScoreBar({score}){
  const c=score>=90?C.green:score>=75?C.accent:score>=60?C.amber:C.red;
  return<div style={{display:"flex",alignItems:"center",gap:6}}><div style={{width:36,height:3,background:C.bgHover,borderRadius:2,overflow:"hidden"}}><div style={{width:`${score}%`,height:"100%",background:c}}/></div><span style={{fontSize:11,color:c,...mono}}>{score}</span></div>;
}
function Progress({pct,color=C.accent,h=4}){
  return<div style={{height:h,background:C.bgHover,borderRadius:h,overflow:"hidden"}}><div style={{width:`${Math.min(pct,100)}%`,height:"100%",background:color,borderRadius:h,transition:"width .5s ease"}}/></div>;
}
function SectionHeader({title,sub,right}){
  // Plain, professional page title — system font (same FONT stack as the
  // rest of the app), solid dark ink, no italic script face and no rainbow
  // gradient fill. Replaces the previous 'Instrument Serif' italic +
  // gradient-text treatment, which read as decorative/cursive rather than
  // a clean business-app heading.
  return<div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:22,flexWrap:"wrap",gap:10}}><div><p style={{fontSize:21,fontWeight:650,fontFamily:FONT,letterSpacing:"-.3px",lineHeight:1.25,color:C.text}}>{title}</p>{sub&&<p style={{fontSize:13,color:C.textSub,marginTop:3}}>{sub}</p>}</div>{right&&<div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>{right}</div>}</div>;
}
function MetricGrid({metrics}){
  return<div className="responsive-grid" style={{display:"grid",gridTemplateColumns:`repeat(${metrics.length},1fr)`,gap:10,marginBottom:20}}>{metrics.map(({label,value,sub,color})=><div key={label} style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 16px"}}><p style={{fontSize:11,color:C.textSub,marginBottom:5,textTransform:"uppercase",letterSpacing:".5px"}}>{label}</p><p style={{fontSize:24,fontWeight:700,...mono,color:color||C.text,lineHeight:1}}>{value}</p>{sub&&<p style={{fontSize:11,color:C.textSub,marginTop:4}}>{sub}</p>}</div>)}</div>;
}
// Restyled onto the Phase 1 design-system tokens (ui/theme.js's C, see
// linear-brewing-toast.md) — API and behavior are untouched: same props,
// same auto-dismiss timing, same optional {label,onClick} action. Only the
// visual language changed, from a flat colored-fill pill to a white card
// with a colored status icon, matching Modal.js/Tooltip.js's new Apple/iOS
// look (soft shadow, hairline border, FONT) instead of the old Fluent `T`
// palette. The type icon and close glyph were previously empty spans
// (whatever character they held didn't survive some earlier edit); now real
// lucide icons.
function Toast({msg,type,onClose,action}){
  // action is optional — {label, onClick} — so every existing call site
  // that only ever passed (msg,type) keeps working unchanged. Toasts with
  // an action get a bit longer before auto-dismissing, since there's now
  // something to actually read and decide on (e.g. "Undo").
  useEffect(()=>{const t=setTimeout(onClose,action?7000:4500);return()=>clearTimeout(t);},[onClose,action]);
  const c={success:{tc:C.green,Icon:CheckCircle2},error:{tc:C.red,Icon:XCircle},info:{tc:C.accent,Icon:Info}}[type]||{tc:C.accent,Icon:Info};
  const {Icon}=c;
  return<div className="slide-in" style={{position:"fixed",bottom:24,right:24,background:"#fff",border:`1px solid ${C.borderSoft}`,borderRadius:14,padding:"13px 14px 13px 16px",display:"flex",alignItems:"center",gap:11,zIndex:9999,maxWidth:380,fontFamily:FONT,boxShadow:"0 2px 6px rgba(0,0,0,.04), 0 16px 40px rgba(0,0,0,.14)"}}>
    <Icon size={19} color={c.tc} strokeWidth={2} style={{flexShrink:0}}/>
    <span style={{fontSize:13,color:C.text,flex:1,lineHeight:1.4}}>{msg}</span>
    {action&&<button onClick={()=>{action.onClick();onClose();}} style={{background:"none",border:"none",color:c.tc,fontSize:13,fontWeight:650,cursor:"pointer",padding:0,fontFamily:FONT,flexShrink:0}}>{action.label}</button>}
    <button onClick={onClose} aria-label="Dismiss" style={{background:"none",border:"none",color:C.textFaint,cursor:"pointer",padding:2,lineHeight:1,display:"flex",flexShrink:0}}><X size={15} strokeWidth={2}/></button>
  </div>;
}

const statusCfg={Sequenced:{bg:C.accentMuted,tc:C.accent,dot:C.accent},Opened:{bg:C.amberMuted,tc:C.amber,dot:C.amber},Replied:{bg:C.greenMuted,tc:C.green,dot:C.green},Meeting:{bg:C.purpleMuted,tc:C.purple,dot:C.purple},Bounced:{bg:C.redMuted,tc:C.red,dot:C.red}};
const intentCfg={Interested:{bg:C.greenMuted,tc:C.green},Curious:{bg:C.accentMuted,tc:C.accent},"Not now":{bg:C.amberMuted,tc:C.amber}};

//  NAV 
const NAV=[
  {id:"dashboard",icon:LayoutDashboard,label:"Dashboard"},
  {id:"campaigns",icon:Megaphone,label:"Campaigns"},
  {id:"contacts",icon:Users,label:"Contacts"},
  {id:"discover",icon:Compass,label:"Discover"},
  {id:"sequences",icon:GitBranch,label:"Sequences"},
  {id:"workspace",icon:Mail,label:"Workspace",newTab:true},
  {id:"mailmerge",icon:Layers,label:"Mail Merge",newTab:true},
  {id:"templates",icon:FileText,label:"Templates"},
  {id:"linkedin",icon:Link2,label:"LinkedIn"},
  {id:"replies",icon:MessageSquare,label:"Replies"},
  {id:"meetings",icon:Calendar,label:"Meetings"},
  {id:"calendar",icon:Calendar,label:"Calendar"},
  {id:"pipeline",icon:BarChart2,label:"Pipeline"},{id:"warmup",icon:Flame,label:"Email Warmup"},
  {id:"team",icon:Users2,label:"Team"},
  {id:"billing",icon:CreditCard,label:"Billing"},
  {id:"settings",icon:SettingsIcon,label:"Settings"},
];
const TRIAL_BADGE_STYLES={
  TRIAL_NOT_STARTED:{bg:C.bgDeep,fg:C.textSub,label:"Trial not started"},
  TRIAL_ACTIVE:{bg:C.accentMuted,fg:C.accent,label:null},
  TRIAL_EXPIRING:{bg:C.amberMuted,fg:C.amber,label:null},
  TRIAL_EXPIRED:{bg:C.redMuted,fg:C.red,label:"Trial expired"},
};
function TrialBadge({trialStatus,onClick}){
  if(!trialStatus||!trialStatus.status||trialStatus.status==="CONVERTED")return null;
  const cfg=TRIAL_BADGE_STYLES[trialStatus.status]||TRIAL_BADGE_STYLES.TRIAL_ACTIVE;
  const days=trialStatus.daysRemaining;
  const label=cfg.label||`${days??"—"} day${days===1?"":"s"} left in trial`;
  return(
    <button onClick={onClick} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:cfg.bg,color:cfg.fg,border:"none",borderRadius:8,padding:"7px 10px",fontSize:11.5,fontWeight:700,cursor:"pointer",marginBottom:10,fontFamily:"inherit"}}>
      {label}<span style={{opacity:.7}}>&rarr;</span>
    </button>
  );
}
// Phase 2 (app-shell redesign, see linear-brewing-toast.md) — restyled onto
// the Apple/iOS C tokens (same ones Dashboard already uses) instead of the
// old Fluent T palette, plus a new collapsible desktop icon-rail mode. The
// mobile drawer's actual mechanism (off-canvas translateX + backdrop) was
// already solid, so that behavior is kept as-is — only its colors/shapes
// changed to match. All existing props/behavior for callers are unchanged;
// `collapsed`/`onToggleCollapse` are new and optional (default: expanded),
// so nothing breaks for any caller that doesn't pass them.
function Sidebar({active,setActive,connected,onLogout,user,mobileOpen,onCloseMobile,isMobile,trialStatus,collapsed,onToggleCollapse}){
  // Desktop: always visible, permanent. Mobile: off-screen unless the
  // drawer is explicitly open. Computed in JS rather than relying on a
  // CSS media query alone, since this transform also depends on the
  // mobileOpen state, not just screen width.
  const translateX = !isMobile ? "0" : (mobileOpen ? "0" : "-100%");
  const isCollapsed = !isMobile && collapsed;
  return(
    <>
      {/* Backdrop - only rendered/visible when the mobile drawer is open */}
      {isMobile&&mobileOpen&&(
        <div onClick={onCloseMobile} style={{position:"fixed",inset:0,background:"rgba(29,29,31,.4)",backdropFilter:"blur(2px)",WebkitBackdropFilter:"blur(2px)",zIndex:99,transition:"opacity .2s"}}/>
      )}
      <div style={{
        width:isCollapsed?68:210,background:C.bg,
        border:isMobile?"none":`1px solid ${C.borderSoft}`,
        borderRadius:isMobile?0:18,
        overflow:"hidden",
        display:"flex",flexDirection:"column",
        position:"fixed",
        top:isMobile?0:12,left:isMobile?0:12,bottom:isMobile?0:12,
        zIndex:100,
        transform:`translateX(${translateX})`,
        transition:"transform .25s cubic-bezier(.32,.72,0,1), width .18s cubic-bezier(.32,.72,0,1)",
        boxShadow:isMobile?(mobileOpen?"4px 0 24px rgba(16,24,40,.18)":"none"):"0 1px 2px rgba(16,24,40,.04), 0 12px 28px rgba(16,24,40,.06)",
        fontFamily:FONT,
      }}>
        <div style={{padding:isCollapsed?"18px 0 14px":"20px 16px 16px",borderBottom:`1px solid ${C.borderSoft}`,display:"flex",alignItems:"center",justifyContent:isCollapsed?"center":"space-between"}}>
          <div style={{display:"flex",flexDirection:isCollapsed?"row":"column",alignItems:isCollapsed?"center":"flex-start",cursor:"pointer",minWidth:0}} onClick={()=>{window.location.href="https://www.sdrreachout.com/";}} role="link" aria-label="SDR ReachOut home">
            {isCollapsed?(
              // Collapsed rail: icon-only mark, kept exactly as designed
              // (full multi-tone shading — no recoloring/flattening).
              <AppLogo size={32} radius={8}/>
            ):(
              <>
                {/* Expanded: the full wordmark lockup image (icon + "SDR"
                    + "ReachOut", straight from the brand asset — pixel-exact,
                    not a CSS recreation), auto-scaled to a 48px-tall logo. */}
                <img src={wordmarkLockup} alt="SDR ReachOut" style={{height:48,width:"auto",display:"block"}}/>
                <p style={{fontSize:12,fontWeight:600,background:"linear-gradient(135deg,#0084FF,#6A28EF)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",whiteSpace:"nowrap",margin:"8px 0 0",lineHeight:1}}>SDR Platform</p>
              </>
            )}
          </div>
          {isMobile&&(
            <button onClick={onCloseMobile} aria-label="Close menu" style={{background:"transparent",border:"none",color:C.textSub,cursor:"pointer",padding:4,lineHeight:1,display:"flex"}}><X size={20}/></button>
          )}
        </div>
        <nav style={{flex:1,padding:isCollapsed?"10px 10px":"10px 8px",overflowY:"auto",overflowX:"hidden"}}>
          {NAV.map(n=>{
            const Icon=n.icon;
            const isActive=active===n.id;
            const go=()=>{
              if(n.newTab){
                // Phase 5 — named windows ("sdr_workspace"/"sdr_mailmerge")
                // instead of "_blank", so repeat clicks on this sidebar
                // button focus the already-open popup instead of spawning
                // a duplicate. Same-name window.open() calls elsewhere
                // (Sequence Monitor's "Open in Workspace", the reply
                // handoff) already target these same names, so all of
                // Workspace's/Mail Merge's entry points now converge on
                // one window per module.
                const isWorkspace=n.id==="workspace";
                window.open(isWorkspace?"/workspace":"/mailmerge",isWorkspace?"sdr_workspace":"sdr_mailmerge","width=1400,height=900");
                return;
              }
              setActive(n.id);onCloseMobile&&onCloseMobile();
            };
            const btn=(
              <button key={n.id} onClick={go}
                onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background=C.bgHover;}}
                onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="transparent";}}
                style={{width:"100%",display:"flex",alignItems:"center",justifyContent:isCollapsed?"center":"flex-start",gap:9,padding:isCollapsed?"10px":"8px 10px",borderRadius:10,border:"none",background:isActive?C.accentSoft:"transparent",color:isActive?C.accent:C.textSub,cursor:"pointer",marginBottom:2,textAlign:"left",transition:"background .12s ease",fontSize:13,fontWeight:isActive?600:500,fontFamily:FONT}}>
                <Icon size={16} strokeWidth={1.9}/>
                {!isCollapsed&&<span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.label}</span>}
                {!isCollapsed&&n.badge&&<span style={{background:C.accent,color:"#fff",fontSize:10,padding:"1px 5px",borderRadius:99,...mono}}>{n.badge}</span>}
              </button>
            );
            // Collapsed rail: wrap each icon in the shared Tooltip so the
            // label is still reachable on hover, since there's no room for
            // text. Skipped on mobile (isCollapsed is always false there).
            return isCollapsed
              ? <Tooltip key={n.id} label={n.label} side="right">{btn}</Tooltip>
              : btn;
          })}
        </nav>
        <div style={{padding:isCollapsed?"10px 10px":"10px 14px",borderTop:`1px solid ${C.borderSoft}`}}>
          {!isCollapsed&&<TrialBadge trialStatus={trialStatus} onClick={()=>setActive("billing")}/>}
          <div style={{display:"flex",alignItems:"center",justifyContent:isCollapsed?"center":"flex-start",gap:6,marginBottom:8}}>
            <span style={{width:6,height:6,borderRadius:"50%",background:connected?C.green:C.amber,flexShrink:0}} className={connected?"":"pulse"}/>
            {!isCollapsed&&<span style={{fontSize:11,color:connected?C.green:C.amber}}>{connected?"Outlook live":"Outlook offline"}</span>}
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:isCollapsed?"center":"flex-start",gap:8}}>
            <Avatar init={(user?.firstName?.[0]||"A")+(user?.lastName?.[0]||"R")} size={28} color={C.purple}/>
            {!isCollapsed&&(
              <>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:12,fontWeight:500,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.firstName||user?.username||"User"} {user?.lastName||""}</p>
                  <p style={{fontSize:10,color:C.textFaint}}>{user?.role||"User"}</p>
                </div>
                <button onClick={onLogout} title="Logout" style={{background:C.redSoft,border:`1px solid ${C.red}30`,color:C.red,cursor:"pointer",padding:"6px 10px",borderRadius:8,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:2}} onMouseEnter={e=>{e.currentTarget.style.background=C.red;e.currentTarget.style.color="#fff";}} onMouseLeave={e=>{e.currentTarget.style.background=C.redSoft;e.currentTarget.style.color=C.red;}}><LogOut size={14}/><span style={{fontSize:9,fontWeight:600,letterSpacing:".3px"}}>Logout</span></button>
              </>
            )}
          </div>
          {/* Collapse/expand toggle — desktop only, sits under the profile
              row so it doesn't compete with the logo for header space. */}
          {!isMobile&&onToggleCollapse&&(
            <button onClick={onToggleCollapse} title={isCollapsed?"Expand sidebar":"Collapse sidebar"}
              style={{width:"100%",marginTop:10,padding:"7px 0",display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:"transparent",border:`1px solid ${C.borderSoft}`,borderRadius:8,color:C.textFaint,cursor:"pointer"}}
              onMouseEnter={e=>{e.currentTarget.style.background=C.bgHover;e.currentTarget.style.color=C.textSub;}}
              onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=C.textFaint;}}>
              {isCollapsed?<ChevronsRight size={14}/>:<ChevronsLeft size={14}/>}
              {!isCollapsed&&<span style={{fontSize:11,fontWeight:500}}>Collapse</span>}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

//  DASHBOARD
// Redesigned onto the Apple/iOS design language (ui/theme.js's C/FONT —
// same tokens the marketing pages already use) as the first real page of
// the app-wide redesign, per the user's "apply it on the home page so we
// can decide" request. Deliberately self-contained — uses its own inline
// styles rather than the shared T-based Card/Btn/Badge/SectionHeader/
// Avatar/Spinner helpers below, so this doesn't visually change any other
// page that still calls those helpers (Discover, LinkedIn, Pipeline,
// Settings, Toast, TrialBadge). Same data-fetching logic, same state,
// same API calls, same columns/fields — styling only.
function DashStatCard({label,value,sub,color}){
  return(
    <div style={{background:"rgba(255,255,255,.7)",backdropFilter:"blur(20px) saturate(180%)",WebkitBackdropFilter:"blur(20px) saturate(180%)",border:`1px solid ${C.borderSoft}`,borderRadius:16,padding:"16px 18px",boxShadow:"0 1px 2px rgba(0,0,0,.03), 0 6px 18px rgba(0,0,0,.04)"}}>
      <p style={{fontSize:11,fontWeight:600,color:C.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:".4px"}}>{label}</p>
      <p style={{fontSize:24,fontWeight:750,color:color||C.text,letterSpacing:"-.4px",lineHeight:1}}>{value}</p>
      <p style={{fontSize:11.5,color:C.textFaint,marginTop:5}}>{sub}</p>
    </div>
  );
}
function DashCard({title,badge,action,children,style={}}){
  return(
    <div style={{background:"#fff",border:`1px solid ${C.borderSoft}`,borderRadius:18,padding:"20px 22px",boxShadow:"0 1px 2px rgba(0,0,0,.03), 0 8px 22px rgba(0,0,0,.04)",...style}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,gap:10,flexWrap:"wrap"}}>
        <p style={{fontSize:14.5,fontWeight:700,color:C.text,letterSpacing:"-.2px",display:"flex",alignItems:"center",gap:8}}>{title}{badge}</p>
        {action}
      </div>
      {children}
    </div>
  );
}
function DashPill({children,bg,tc}){
  return<span style={{fontSize:10.5,fontWeight:700,color:tc,background:bg,padding:"3px 9px",borderRadius:999,whiteSpace:"nowrap"}}>{children}</span>;
}
function DashLinkBtn({children,onClick,primary}){
  return(
    <button onClick={onClick} style={{background:primary?C.accent:"transparent",color:primary?"#fff":C.accent,border:primary?"none":`1px solid ${C.border}`,borderRadius:999,padding:"6px 14px",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:FONT,whiteSpace:"nowrap",transition:"background .15s ease, opacity .15s ease"}}>{children}</button>
  );
}
function DashAvatar({init,size=30,color=C.accent,bg}){
  return<div style={{width:size,height:size,borderRadius:"50%",background:bg||color+"1A",display:"flex",alignItems:"center",justifyContent:"center",fontSize:Math.round(size*.36),fontWeight:700,color,flexShrink:0,fontFamily:FONT}}>{init}</div>;
}
function Dashboard({setActive,outlookConnected,user}){
  const [stats,setStats]=useState(null);
  const [campaigns,setCampaigns]=useState([]);
  const [sequences,setSequences]=useState([]);
  const [replies,setReplies]=useState([]);
  const [meetings,setMeetings]=useState([]);
  const [loading,setLoading]=useState(true);
  const hr=new Date().getHours();
  const greeting=hr<12?"Good morning":hr<17?"Good afternoon":"Good evening";
  const firstName=user?.firstName||(user?.name?.split(" ")[0])||user?.username||"there";
  const load=useCallback(()=>{
    Promise.all([
      API.getStats().catch(()=>({})),
      API.getCampaigns().catch(()=>[]),
      API.getSequences().catch(()=>[]),
      API.getReplies().catch(()=>[]),
      API.getMeetings().catch(()=>[]),
    ]).then(([s,c,sq,r,m])=>{
      setStats(s);setCampaigns(Array.isArray(c)?c:[]);setSequences(Array.isArray(sq)?sq:[]);
      setReplies(Array.isArray(r)?r:[]);setMeetings(Array.isArray(m)?m:[]);setLoading(false);
    });
  },[]);
  useEffect(()=>{ load(); const t=setInterval(load,30000); return()=>clearInterval(t); },[load]);
  if(loading&&!stats)return(
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"60vh",flexDirection:"column",gap:14,fontFamily:FONT}}>
      <div style={{width:26,height:26,border:`2.5px solid ${C.borderSoft}`,borderTopColor:C.accent,borderRadius:"50%",animation:"spin .7s linear infinite"}}/>
      <p style={{color:C.textSub,fontSize:13.5}}>Loading dashboard&hellip;</p>
    </div>
  );
  const s=stats||{};
  const totalSent=s.sent||0;
  const openRate=totalSent>0?((s.opened||0)/totalSent*100).toFixed(1)+"%":s.openRate||"0%";
  const replyRate=totalSent>0?((s.replied||0)/totalSent*100).toFixed(1)+"%":s.replyRate||"0%";
  return(
    <div style={{fontFamily:FONT}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          {/* Solid, professional dashboard greeting — same system FONT as
              every other heading, no rainbow gradient fill. */}
          <p style={{fontSize:28,fontWeight:700,letterSpacing:"-.4px",lineHeight:1.15,color:C.text}}>{greeting}, {firstName}</p>
          <p style={{fontSize:13.5,color:C.textSub,marginTop:5}}>Real-time outreach command center</p>
        </div>
        <DashLinkBtn onClick={load}>Refresh</DashLinkBtn>
      </div>

      <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:12,marginBottom:22}}>
        <DashStatCard label="Total Leads" value={s.leads||0} color={C.accent} sub="in database"/>
        <DashStatCard label="Campaigns" value={s.campaigns||0} color={C.cyan} sub={(s.activeCampaigns||0)+" active"}/>
        <DashStatCard label="Sequences" value={sequences.length} color={C.purple} sub="automated flows"/>
        <DashStatCard label="Emails Sent" value={totalSent} sub="total outreach"/>
        <DashStatCard label="Open Rate" value={openRate} color={C.amber} sub={(s.opened||0)+" opens"}/>
        <DashStatCard label="Reply Rate" value={replyRate} color={C.green} sub={(s.replied||0)+" replies"}/>
      </div>

      <DashCard title="Campaign Performance" action={<DashLinkBtn onClick={()=>setActive("campaigns")}>View all</DashLinkBtn>} style={{marginBottom:18}}>
        {campaigns.length===0?(
          <div style={{textAlign:"center",padding:"28px 0"}}>
            <p style={{fontSize:13.5,color:C.textSub,marginBottom:12}}>No campaigns yet</p>
            <DashLinkBtn primary onClick={()=>setActive("campaigns")}>Create campaign</DashLinkBtn>
          </div>
        ):(
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5}}>
              <thead><tr style={{borderBottom:`1px solid ${C.borderSoft}`}}>
                {["Campaign","Status","Leads","Sent","Opens","Replies","Open Rate","Reply Rate","Progress"].map(h=>(
                  <th key={h} style={{padding:"7px 10px",textAlign:"left",color:C.textFaint,fontWeight:600,whiteSpace:"nowrap",fontSize:10.5,textTransform:"uppercase",letterSpacing:".3px"}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {campaigns.map((c,i)=>{
                  const pct=Number(c.leads)>0?Math.round((Number(c.sent)||0)/Number(c.leads)*100):0;
                  const oR=Number(c.sent)>0?(((Number(c.opened)||0)/Number(c.sent))*100).toFixed(1)+"%":"0%";
                  const rR=Number(c.sent)>0?(((Number(c.replied)||0)/Number(c.sent))*100).toFixed(1)+"%":"0%";
                  const stBg=c.status==="Active"?C.greenSoft:c.status==="Paused"?C.amberSoft:C.bgAlt;
                  const stTc=c.status==="Active"?C.green:c.status==="Paused"?C.amber:C.textSub;
                  return(
                    <tr key={c.id||i} style={{borderBottom:i<campaigns.length-1?`1px solid ${C.borderSoft}`:"none"}}>
                      <td style={{padding:"10px 10px",fontWeight:600,color:C.text,maxWidth:150,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</td>
                      <td style={{padding:"10px 10px"}}><DashPill bg={stBg} tc={stTc}>{c.status||"Draft"}</DashPill></td>
                      <td style={{padding:"10px 10px",color:C.textSub}}>{c.leads||0}</td>
                      <td style={{padding:"10px 10px",color:C.textSub}}>{c.sent||0}</td>
                      <td style={{padding:"10px 10px",color:C.amber,fontWeight:600}}>{c.opened||0}</td>
                      <td style={{padding:"10px 10px",color:C.green,fontWeight:600}}>{c.replied||0}</td>
                      <td style={{padding:"10px 10px",color:C.textSub}}>{oR}</td>
                      <td style={{padding:"10px 10px",color:C.textSub}}>{rR}</td>
                      <td style={{padding:"10px 10px",minWidth:90}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <div style={{flex:1,height:5,background:C.bgAlt,borderRadius:3,overflow:"hidden"}}>
                            <div style={{width:pct+"%",height:"100%",background:C.accent,borderRadius:3}}/>
                          </div>
                          <span style={{fontSize:10.5,color:C.textFaint}}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DashCard>

      <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginBottom:16}}>
        <DashCard title="Sequences" action={<DashLinkBtn onClick={()=>setActive("sequences")}>View all</DashLinkBtn>}>
          {sequences.length===0?(<p style={{fontSize:12.5,color:C.textFaint,padding:"12px 0"}}>No sequences yet.</p>):(
            sequences.slice(0,5).map((sq,i)=>(
              <div key={sq.id||i} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:i<Math.min(sequences.length,5)-1?`1px solid ${C.borderSoft}`:"none"}}>
                <div style={{width:32,height:32,borderRadius:9,background:C.purpleSoft,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{fontSize:12.5,fontWeight:700,color:C.purple}}>{(sq.steps||[]).length}</span>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:12.5,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{sq.name}</p>
                  <p style={{fontSize:11,color:C.textFaint}}>{(sq.steps||[]).length} steps</p>
                </div>
                <DashPill bg={sq.status==="Active"?C.greenSoft:C.bgAlt} tc={sq.status==="Active"?C.green:C.textSub}>{sq.status||"Active"}</DashPill>
              </div>
            ))
          )}
        </DashCard>

        <DashCard title="Hot Replies" badge={<DashPill bg={C.greenSoft} tc={C.green}>{replies.length}</DashPill>} action={<DashLinkBtn onClick={()=>setActive("replies")}>Handle</DashLinkBtn>}>
          {replies.length===0?(<p style={{fontSize:12.5,color:C.textFaint,padding:"12px 0"}}>No replies yet.</p>):(
            replies.slice(0,4).map((r,i)=>{
              const ic=(intentCfg&&intentCfg[r.intent])?{bg:intentCfg[r.intent].bg===C.greenMuted?C.greenSoft:intentCfg[r.intent].bg===C.accentMuted?C.accentSoft:C.amberSoft,tc:intentCfg[r.intent].tc===C.green?C.green:intentCfg[r.intent].tc===C.accent?C.accent:C.amber}:{bg:C.bgAlt,tc:C.textSub};
              return(
                <div key={r.id||i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"9px 0",borderBottom:i<3?`1px solid ${C.borderSoft}`:"none"}}>
                  <DashAvatar init={((r.name||"?")+" ").split(" ").map(w=>w[0]).join("").slice(0,2)} size={28} color={ic.tc} bg={ic.bg}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                      <span style={{fontSize:12.5,fontWeight:600,color:C.text}}>{r.name}</span>
                      <DashPill bg={ic.bg} tc={ic.tc}>{r.intent}</DashPill>
                    </div>
                    <p style={{fontSize:11.5,color:C.textFaint,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.body}</p>
                  </div>
                </div>
              );
            })
          )}
        </DashCard>

        <DashCard title="Upcoming Meetings" action={<DashLinkBtn onClick={()=>setActive("meetings")}>View all</DashLinkBtn>}>
          {meetings.length===0?(<p style={{fontSize:12.5,color:C.textFaint,padding:"12px 0"}}>No meetings booked yet.</p>):(
            meetings.slice(0,4).map((m,i)=>(
              <div key={m.id||i} style={{display:"flex",gap:12,padding:"9px 0",borderBottom:i<3?`1px solid ${C.borderSoft}`:"none",alignItems:"center"}}>
                <div style={{width:36,height:36,borderRadius:9,background:C.accentSoft,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <span style={{fontSize:7,color:C.accent,textTransform:"uppercase",letterSpacing:1,fontWeight:700}}>{(m.date||"").split(" ")[0]}</span>
                  <span style={{fontSize:14,fontWeight:750,color:C.accent,lineHeight:1}}>{(m.date||"").split(" ")[1]}</span>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:12.5,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.name}</p>
                  <p style={{fontSize:11.5,color:C.textFaint}}>{m.type} &middot; {m.time}</p>
                </div>
                <DashPill bg={m.status==="Confirmed"?C.greenSoft:C.amberSoft} tc={m.status==="Confirmed"?C.green:C.amber}>{m.status}</DashPill>
              </div>
            ))
          )}
        </DashCard>
      </div>
    </div>
  );
}

//  DISCOVER
function Discover({addToast,settings}){
  const [leads,setLeads]=useState([]);
  const [q,setQ]=useState("");
  const [sf,setSf]=useState("all");
  const [loading,setLoading]=useState(false);
  const [apolloForm,setApolloForm]=useState({title:"",location:"",limit:10});
  const [apolloResults,setApolloResults]=useState([]);
  const [apolloLoading,setApolloLoading]=useState(false);
  const [importing,setImporting]=useState(false);

  useEffect(()=>{API.getLeads().then(setLeads).catch(()=>{});},[]);

  const filtered=leads.filter(l=>(sf==="all"||l.status.toLowerCase()===sf)&&(l.name.toLowerCase().includes(q.toLowerCase())||l.company.toLowerCase().includes(q.toLowerCase())));

  const searchApollo=async()=>{
    if(!apolloForm.title&&!apolloForm.location){addToast("Enter a job title or location to search","error");return;}
    setApolloLoading(true);setApolloResults([]);
    try{
      const res=await API.apolloSearch({...apolloForm,apolloKey:settings?.apolloKey});
      if(res.prospects&&res.prospects.length>0){
        setApolloResults(res.prospects);
        addToast(`Found ${res.prospects.length} prospects from Apollo`,"success");
      }else{addToast("No prospects found  try different filters","info");}
    }catch(e){addToast("Apollo search failed: "+e.message,"error");}
    setApolloLoading(false);
  };

  const importProspects=async()=>{
    if(!apolloResults.length){addToast("Search Apollo first","error");return;}
    setImporting(true);
    try{
      const batchName="Apollo Search "+new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})+" ("+apolloResults.length+")";
      for(const p of apolloResults){
        await fetch('/api/leads',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({...p,contactFile:batchName})}).catch(()=>{});
      }
      const updated=await API.getLeads();
      setLeads(updated);
      setApolloResults([]);
      addToast(` Imported ${apolloResults.length} prospects into "${batchName}"`,"success");
    }catch(e){addToast(e.message,"error");}
    setImporting(false);
  };

  const refreshSearch=()=>{
    setLoading(true);
    setTimeout(()=>{setLoading(false);addToast("Found 25 new SaaS leads  ready to enrich","info");},1800);
  };

  return(
    <div className="fade-up">
      <SectionHeader title="Lead Discovery" sub={`${leads.length} enriched  Apollo.io connected`}
        right={<><Btn sm onClick={refreshSearch} disabled={loading}>{loading?<><Spinner size={11}/> Searching</>:" Refresh"}</Btn><Btn sm primary>+ Enrich selected</Btn></>}
      />

      <Card style={{marginBottom:16,border:`1px solid ${C.accent}44`}}>
        <p style={{fontSize:13,fontWeight:600,marginBottom:12}}> Apollo.io Prospect Search</p>
        <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
          <div><Label>Job title (comma separated)</Label><input value={apolloForm.title||""} onChange={e=>setApolloForm(p=>({...p,title:e.target.value}))} placeholder="e.g. VP Engineering, CTO"/></div>
          <div><Label>Exclude title</Label><input value={apolloForm.excludeTitle||""} onChange={e=>setApolloForm(p=>({...p,excludeTitle:e.target.value}))} placeholder="e.g. Intern, Assistant"/></div>
          <div><Label>Keywords</Label><input value={apolloForm.keywords||""} onChange={e=>setApolloForm(p=>({...p,keywords:e.target.value}))} placeholder="e.g. QA, DevOps, SaaS"/></div>
        </div>
        <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
          <div><Label>Location (comma separated)</Label><input value={apolloForm.location||""} onChange={e=>setApolloForm(p=>({...p,location:e.target.value}))} placeholder="e.g. San Francisco, Austin"/></div>
          <div><Label>Country</Label><input value={apolloForm.country||""} onChange={e=>setApolloForm(p=>({...p,country:e.target.value}))} placeholder="e.g. USA, India, UK"/></div>
          <div><Label>Company name</Label><input value={apolloForm.companyName||""} onChange={e=>setApolloForm(p=>({...p,companyName:e.target.value}))} placeholder="e.g. HubSpot, Salesforce"/></div>
        </div>
        <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
          <div><Label>Seniority level</Label>
            <select value={apolloForm.seniority||""} onChange={e=>setApolloForm(p=>({...p,seniority:e.target.value}))}>
              <option value="">Any seniority</option>
              <option value="owner">Owner</option>
              <option value="founder">Founder</option>
              <option value="c_suite">C-Suite (CEO, CTO, CFO)</option>
              <option value="partner">Partner</option>
              <option value="vp">VP (Vice President)</option>
              <option value="head">Head</option>
              <option value="director">Director</option>
              <option value="manager">Manager</option>
              <option value="senior">Senior Individual Contributor</option>
              <option value="entry">Entry Level</option>
              <option value="intern">Intern</option>
            </select>
          </div>
          <div><Label>Company size</Label>
            <select value={apolloForm.companySize||""} onChange={e=>setApolloForm(p=>({...p,companySize:e.target.value}))}>
              <option value="">Any size</option>
              <option value="1-10">110 (Micro)</option>
              <option value="11-50">1150 (Startup)</option>
              <option value="51-200">51200 (Small)</option>
              <option value="201-500">201500 (Mid-market)</option>
              <option value="501-1000">5011,000</option>
              <option value="1001-5000">1,0015,000</option>
              <option value="5001-10000">5,00110,000</option>
              <option value="5000+">10,000+ (Enterprise)</option>
            </select>
          </div>
          <div><Label>Annual revenue</Label>
            <select value={apolloForm.revenue||""} onChange={e=>setApolloForm(p=>({...p,revenue:e.target.value}))}>
              <option value="">Any revenue</option>
              <option value="Under $1M">Under $1M</option>
              <option value="$1M-$10M">$1M  $10M</option>
              <option value="$10M-$50M">$10M  $50M</option>
              <option value="$50M-$200M">$50M  $200M</option>
              <option value="$200M-$1B">$200M  $1B</option>
              <option value="$200M+">$1B+</option>
            </select>
          </div>
        </div>
        <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
          <div><Label>Industry</Label>
            <select value={apolloForm.industry||""} onChange={e=>setApolloForm(p=>({...p,industry:e.target.value}))}>
              <option value="">Any industry</option>
              <option>Information Technology</option>
              <option>Computer Software</option>
              <option>SaaS</option>
              <option>Internet</option>
              <option>Financial Services</option>
              <option>FinTech</option>
              <option>Healthcare</option>
              <option>HealthTech</option>
              <option>Biotechnology</option>
              <option>Pharmaceuticals</option>
              <option>E-Commerce</option>
              <option>Retail</option>
              <option>Education</option>
              <option>EdTech</option>
              <option>Real Estate</option>
              <option>Telecommunications</option>
              <option>Media & Entertainment</option>
              <option>Marketing & Advertising</option>
              <option>Legal Services</option>
              <option>Management Consulting</option>
              <option>Human Resources</option>
              <option>Staffing & Recruiting</option>
              <option>Logistics & Supply Chain</option>
              <option>Manufacturing</option>
              <option>Automotive</option>
              <option>Aerospace & Defense</option>
              <option>Energy & Utilities</option>
              <option>CleanTech</option>
              <option>Non-profit</option>
              <option>Government</option>
            </select>
          </div>
          <div><Label>Department</Label>
            <select value={apolloForm.department||""} onChange={e=>setApolloForm(p=>({...p,department:e.target.value}))}>
              <option value="">Any department</option>
              <option value="engineering">Engineering</option>
              <option value="product_management">Product Management</option>
              <option value="sales">Sales</option>
              <option value="marketing">Marketing</option>
              <option value="finance">Finance</option>
              <option value="operations">Operations</option>
              <option value="human_resources">Human Resources</option>
              <option value="customer_success">Customer Success</option>
              <option value="information_technology">IT</option>
              <option value="legal">Legal</option>
              <option value="design">Design</option>
              <option value="data_science">Data Science</option>
              <option value="business_development">Business Development</option>
            </select>
          </div>
          <div><Label>Email status</Label>
            <select value={apolloForm.emailStatus||""} onChange={e=>setApolloForm(p=>({...p,emailStatus:e.target.value}))}>
              <option value="">Any</option>
              <option value="verified">Verified</option>
              <option value="likely_to_engage">Likely to engage</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </div>
        </div>
        <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div><Label>Technologies used</Label>
            <select value={apolloForm.technology||""} onChange={e=>setApolloForm(p=>({...p,technology:e.target.value}))}>
              <option value="">Any technology</option>
              <option>Salesforce</option>
              <option>HubSpot</option>
              <option>Marketo</option>
              <option>Slack</option>
              <option>Jira</option>
              <option>GitHub</option>
              <option>AWS</option>
              <option>Azure</option>
              <option>Google Cloud</option>
              <option>React</option>
              <option>Node.js</option>
              <option>Python</option>
              <option>Kubernetes</option>
              <option>Docker</option>
              <option>Snowflake</option>
              <option>Zendesk</option>
              <option>Intercom</option>
              <option>Stripe</option>
            </select>
          </div>
          <div><Label>Limit (max 50)</Label>
            <input type="number" min={1} max={50} value={apolloForm.limit||10} onChange={e=>setApolloForm(p=>({...p,limit:parseInt(e.target.value)||10}))}/>
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <Btn sm primary onClick={searchApollo} disabled={apolloLoading}>{apolloLoading?<><Spinner size={11}/> Searching Apollo</>:" Search Apollo"}</Btn>
          {apolloResults.length>0&&<Btn sm primary onClick={importProspects} disabled={importing}>{importing?<><Spinner size={11}/> Importing</>:` Import ${apolloResults.length} prospects`}</Btn>}
          <Btn sm onClick={()=>{setApolloForm({title:"",excludeTitle:"",keywords:"",location:"",country:"",companyName:"",seniority:"",companySize:"",revenue:"",limit:10});setApolloResults([]);}}> Clear</Btn>
          {apolloResults.length>0&&<span style={{fontSize:12,color:C.textSub}}>{apolloResults.length} prospects ready to import</span>}
        </div>
        {apolloResults.length>0&&(
          <div style={{marginTop:12,maxHeight:220,overflowY:"auto"}}>
            <div className="hide-on-mobile" style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1.5fr 1fr",gap:8,padding:"7px 10px",background:C.bgHover,borderRadius:6,marginBottom:4}}>
              {["Name","Title","Company","Location"].map(h=><span key={h} style={{fontSize:10,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px"}}>{h}</span>)}
            </div>
            {apolloResults.map((p,i)=>(
              <div key={p.id} className="responsive-grid" style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1.5fr 1fr",gap:8,padding:"7px 10px",borderBottom:i<apolloResults.length-1?`1px solid ${C.border}`:"none",alignItems:"center"}}>
                <div style={{display:"flex",alignItems:"center",gap:7}}>
                  <Avatar init={p.name.split(" ").map(w=>w[0]).join("").slice(0,2)} size={24} color={C.accent}/>
                  <span style={{fontSize:12,fontWeight:500}}>{p.name}</span>
                </div>
                <span style={{fontSize:11,color:C.textSub}}>{p.title.slice(0,22)}{p.title.length>22?"":""}</span>
                <span style={{fontSize:11,color:C.textSub}}>{p.company.slice(0,18)}{p.company.length>18?"":""}</span>
                <span style={{fontSize:11,color:C.textSub}}>{p.location||""}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
        <input placeholder="Search name or company" value={q} onChange={e=>setQ(e.target.value)} style={{flex:1,minWidth:180}}/>
        {["all","sequenced","opened","replied","meeting"].map(s=>(
          <button key={s} onClick={()=>setSf(s)} style={{padding:"6px 13px",borderRadius:8,border:`1px solid ${sf===s?C.accent:C.border}`,background:sf===s?C.accentMuted:"transparent",color:sf===s?C.accent:C.textSub,fontSize:12,cursor:"pointer",textTransform:"capitalize"}}>{s}</button>
        ))}
      </div>
      <Card style={{padding:0,overflow:"hidden"}}>
        <div className="hide-on-mobile" style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1.2fr 90px 110px 80px",gap:8,padding:"9px 16px",background:C.bgHover,borderBottom:`1px solid ${C.border}`}}>
          {["Name","Title  Company","Email","Tier","Status","Score"].map(h=><span key={h} style={{fontSize:10,color:C.textSub,textTransform:"uppercase",letterSpacing:".6px",fontWeight:500}}>{h}</span>)}
        </div>
        {filtered.map((l,i)=>{
          const sc=statusCfg[l.status]||statusCfg.Sequenced;
          const tc=l.tier==="T1"?C.accent:C.purple;
          return(
            <div key={l.id} className="responsive-grid" style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1.2fr 90px 110px 80px",gap:8,padding:"10px 16px",borderBottom:i<filtered.length-1?`1px solid ${C.border}`:"none",alignItems:"center",cursor:"pointer",transition:"background .1s"}} onMouseEnter={e=>e.currentTarget.style.background=C.bgHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}><Avatar init={l.name.split(" ").map(w=>w[0]).join("").slice(0,2)} size={26} color={tc}/><span style={{fontSize:13,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.name}</span></div>
              <div style={{minWidth:0,overflow:"hidden"}}><p style={{fontSize:12,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.title}</p><p style={{fontSize:11,color:C.textSub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.company}</p></div>
              <span style={{fontSize:11,color:C.textSub,...mono,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:0}}>{l.email}</span>
              <Badge bg={l.tier==="T1"?C.accentMuted:C.purpleMuted} tc={tc}>{l.tier}</Badge>
              <div style={{display:"flex",alignItems:"center",gap:5}}><span style={{width:5,height:5,borderRadius:"50%",background:sc.dot,flexShrink:0}}/><Badge bg={sc.bg} tc={sc.tc}>{l.status}</Badge></div>
              <ScoreBar score={l.score}/>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

//  REPLIES
function Replies({outlookConnected,settings,addToast}){
  const [replies,setReplies]=useState([]);
  const [drafts,setDrafts]=useState({});
  const [loading,setLoading]=useState({});
  const [sending,setSending]=useState({});
  const [briefing,setBriefing]=useState({});
  const [fetching,setFetching]=useState(false);

  useEffect(()=>{API.getReplies().then(setReplies).catch(()=>{});},[]);

  const fetchLive=async()=>{
    if(!outlookConnected){addToast("Connect Outlook to fetch live replies","error");return;}
    setFetching(true);
    try{
      const msgs=await API.fetchInbox();
      const mapped=msgs.map((m,i)=>({id:1000+i,name:m.from?.emailAddress?.name||"Unknown",company:"",title:"",time:"Live from Outlook",body:m.bodyPreview||"",intent:"Curious",score:70}));
      setReplies(p=>[...replies,...mapped]);
      addToast("Fetched "+msgs.length+" unread emails from Outlook","success");
    }catch(e){addToast(e.message,"error");}
    setFetching(false);
  };

  const draft=async(r)=>{
    setLoading(p=>({...p,[r.id]:true}));
    try{const res=await API.genReplyDraft(r,settings);setDrafts(p=>({...p,[r.id]:res.draft}));addToast("Draft ready","success");}
    catch(e){addToast("Error: "+e.message,"error");}
    setLoading(p=>({...p,[r.id]:false}));
  };

  const send=async(r)=>{
    if(!outlookConnected){addToast("Connect Outlook first","error");return;}
    const body=drafts[r.id];
    if(!body){addToast("Draft a reply first","error");return;}
    setSending(p=>({...p,[r.id]:true}));
    try{
      await API.sendEmail({to:r.email||"",subject:`Re: Following up  ${settings.company}`,body,fromName:settings.sdrName});
      addToast(` Reply sent to ${r.name} via Outlook`,"success");
    }catch(e){addToast(e.message,"error");}
    setSending(p=>({...p,[r.id]:false}));
  };

  const getMeetingBrief=async(r)=>{
    setBriefing(p=>({...p,[r.id]:true}));
    try{
      const meet=await API.getMeetings().then(m=>m.find(x=>x.name===r.name));
      if(!meet){addToast("No meeting found for this contact","error");return;}
      const res=await API.getMeetingBrief(meet.id);
      addToast("Brief: "+res.brief.slice(0,100)+"","info");
    }catch(e){addToast(e.message,"error");}
    setBriefing(p=>({...p,[r.id]:false}));
  };

  return(
    <div className="fade-up">
      <SectionHeader title="Reply Inbox" sub="AI-routed  intent-classified  one-click Outlook send"
        right={<><Btn sm onClick={fetchLive} disabled={fetching}>{fetching?<><Spinner size={11}/> Syncing</>:" Sync Outlook inbox"}</Btn><Badge bg={C.greenMuted} tc={C.green} dot>{replies.length} replies</Badge></>}
      />
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {replies.map(r=>{
          const ic=intentCfg[r.intent]||intentCfg.Curious;
          return(
            <Card key={r.id} style={{border:`1px solid ${r.intent==="Interested"?C.green+"44":C.border}`}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:12}}>
                <Avatar init={r.name.split(" ").map(w=>w[0]).join("").slice(0,2)} size={38} color={ic.tc}/>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                    <span style={{fontSize:14,fontWeight:600}}>{r.name}</span>
                    {r.title&&<span style={{fontSize:12,color:C.textSub}}>{r.title}{r.company?`  ${r.company}`:""}</span>}
                    <Badge bg={ic.bg} tc={ic.tc}>{r.intent}</Badge>
                    <span style={{fontSize:11,color:C.textSub,marginLeft:"auto"}}>{r.time}</span>
                  </div>
                  <p style={{fontSize:13,color:C.textSub,lineHeight:1.5}}>{r.body}</p>
                </div>
              </div>
              <div style={{display:"flex",gap:8,padding:"10px 0",borderTop:`1px solid ${C.border}`,flexWrap:"wrap",alignItems:"center"}}>
                <span style={{fontSize:12,color:C.textSub,flex:1}}> AI suggests: {r.intent==="Interested"?"Book meeting":r.intent==="Curious"?"Send more info":"Snooze 90 days"}</span>
                <Btn sm onClick={()=>draft(r)} disabled={!!loading[r.id]}>{loading[r.id]?<><Spinner size={11}/> Drafting</>:" Draft reply"}</Btn>
                {r.intent==="Interested"&&<Btn sm primary onClick={()=>getMeetingBrief(r)} disabled={!!briefing[r.id]}> Meeting brief</Btn>}
                <Btn sm ghost> Snooze</Btn>
              </div>
              {drafts[r.id]&&(
                <div style={{marginTop:10,background:C.bgHover,borderRadius:8,padding:12}}>
                  <p style={{fontSize:10,color:C.accent,textTransform:"uppercase",letterSpacing:".6px",marginBottom:6}}>AI-drafted reply</p>
                  <textarea rows={4} value={drafts[r.id]} onChange={e=>setDrafts(p=>({...p,[r.id]:e.target.value}))}/>
                  <div style={{display:"flex",gap:8,marginTop:8}}>
                    <Btn sm primary onClick={()=>send(r)} disabled={!!sending[r.id]}>{sending[r.id]?<><Spinner size={11}/> Sending</>:" Send via Outlook"}</Btn>
                    <Btn sm ghost onClick={()=>navigator.clipboard.writeText(drafts[r.id]).then(()=>addToast("Copied","success"))}>Copy</Btn>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

//  MEETINGS 
function Meetings({outlookConnected,settings,addToast,setActive=()=>{}}){
  const [leads,setLeads]=useState([]);
  const [meetings,setMeetings]=useState([]);
  const [form,setForm]=useState({lead:"",date:"",time:"09:00"});
  const {timezone,setTimezone,tzLabel,tzNow}=useTz();
  const [booking,setBooking]=useState(false);
  const [show,setShow]=useState(false);
  const [briefingId,setBriefingId]=useState(null);
  const [brief,setBrief]=useState({});

  useEffect(()=>{
    API.getLeads().then(setLeads).catch(()=>{});
    API.getMeetings().then(setMeetings).catch(()=>{});
  },[]);

  const book=async()=>{
    if(!outlookConnected){addToast("Connect Outlook first","error");return;}
    // leads' ids are Mongo ObjectId strings (see /api/leads), not integers —
    // this used to compare against parseInt(form.lead), which is NaN for a
    // hex id and so never matched anything; the whole booking flow was
    // silently unreachable past this point until now.
    const lead=leads.find(l=>l.id===form.lead);
    if(!lead||!form.date){addToast("Select a lead and date","error");return;}
    setBooking(true);
    try{
      const endHour=String(parseInt(form.time.split(":")[0])+1).padStart(2,"0");
      const start=`${form.date}T${form.time}:00`;
      const end=`${form.date}T${endHour}:${form.time.split(":")[1]}:00`;
      await API.bookMeeting({
        subject:`Discovery Call  ${lead.name} & ${settings.sdrName} | ${settings.company}`,
        start, end,
        attendeeEmail:lead.email,
        attendeeName:lead.name,
        body:`Hi ${lead.firstName||lead.name},<br><br>Looking forward to our discovery call!<br><br>Best,<br>${settings.sdrName}`
      });
      // Matches the real Meeting schema (db.js) — same model/API the
      // Calendar page reads, so a meeting booked here shows up there too.
      const m=await API.createMeeting({
        title:`Discovery Call  ${lead.name}${lead.company?` (${lead.company})`:""}`,
        start, end,
        attendees:lead.email?[lead.email]:[],
        location:"Outlook",
        type:"Discovery call",
        status:"Pending",
      });
      setMeetings(p=>[...p,m]);
      addToast(` Outlook invite sent to ${lead.name}`,"success");setShow(false);
    }catch(e){addToast(e.message,"error");}
    setBooking(false);
  };

  const getPrepBrief=async(m)=>{
    setBriefingId(m.id);
    try{
      const res=await API.getMeetingBrief(m.id);
      setBrief(p=>({...p,[m.id]:res.brief}));
    }catch(e){addToast(e.message,"error");}
    setBriefingId(null);
  };

  const cancelMeeting=async(id)=>{
    try{await API.deleteMeeting(id);setMeetings(p=>p.filter(m=>m.id!==id));addToast("Meeting cancelled","info");}
    catch(e){addToast(e.message,"error");}
  };

  return(
    <div className="fade-up">
      <SectionHeader title="Meeting Booking" sub="Outlook Calendar  Microsoft Teams  Auto-booking"
        right={<><Btn sm onClick={()=>setActive("calendar")}> View Calendar</Btn><Btn sm primary onClick={()=>setShow(!show)}>+ Book meeting</Btn></>}
      />
      {show&&(
        <Card style={{marginBottom:16}}>
          <p style={{fontSize:13,fontWeight:600,marginBottom:12}}>Book Outlook meeting</p>
          <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
            <div><Label>Lead</Label><select value={form.lead} onChange={e=>setForm(p=>({...p,lead:e.target.value}))}><option value="">Choose lead</option>{leads.map(l=><option key={l.id} value={l.id}>{l.name}  {l.company}</option>)}</select></div>
            <div><Label>Date</Label><input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))}/></div>
            <div><Label>Time</Label><input type="time" value={form.time} onChange={e=>setForm(p=>({...p,time:e.target.value}))}/></div>
          </div>
          <div style={{marginBottom:12}}>
            <Label>Timezone</Label>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:"#fff",borderRadius:8,border:`1px solid ${C.border}`,boxShadow:"0 1px 4px rgba(0,0,0,.06)",flexWrap:"wrap",rowGap:10}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:38,height:38,borderRadius:8,background:C.accentMuted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}></div>
                <div>
                  <p style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:1}}>{tzLabel}</p>
                  <p style={{fontSize:11,color:C.textSub}}>Meeting invite will be sent in this timezone</p>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{textAlign:"right"}}>
                  <p style={{fontSize:11,color:C.textMuted,marginBottom:1}}>Current time</p>
                  <p style={{fontSize:14,fontWeight:700,color:C.accent,fontFamily:"monospace"}}>{tzNow}</p>
                </div>
                <div style={{width:1,height:32,background:C.border}}/>
                <TimezoneSelector/>
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}><Btn sm primary onClick={book} disabled={booking}>{booking?<><Spinner size={11}/> Booking</>:" Create Outlook invite"}</Btn><Btn sm onClick={()=>setShow(false)}>Cancel</Btn></div>
        </Card>
      )}
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {meetings.map(m=>{
          const st=m.start?new Date(m.start):null;
          return(
          <Card key={m.id}>
            <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
              <div style={{width:48,height:48,borderRadius:9,background:C.accentMuted,border:`1px solid ${C.accent}30`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <span style={{fontSize:8,color:C.accent,textTransform:"uppercase",letterSpacing:1}}>{st?st.toLocaleDateString([],{month:"short"}):""}</span>
                <span style={{fontSize:18,fontWeight:700,color:C.accent,...mono,lineHeight:1}}>{st?st.getDate():""}</span>
              </div>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:2}}>
                  <span style={{fontSize:13,fontWeight:500}}>{m.title}</span>
                  <Badge bg={m.status==="Confirmed"?C.greenMuted:C.amberMuted} tc={m.status==="Confirmed"?C.green:C.amber} dot sm>{m.status}</Badge>
                </div>
                <p style={{fontSize:12,color:C.textSub}}>{m.type}  {st?st.toLocaleTimeString([],{hour:"numeric",minute:"2-digit"}):""} <span style={{fontSize:10,background:C.accentMuted,color:C.accent,padding:"1px 6px",borderRadius:3,marginLeft:4}}>{tzLabel.split(" ")[0].trim()}</span></p>
                {brief[m.id]&&<div style={{marginTop:8,background:C.bgHover,borderRadius:7,padding:10,fontSize:12,color:C.textSub,whiteSpace:"pre-line"}}>{brief[m.id]}</div>}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5,flexShrink:0}}>
                <Btn sm onClick={()=>getPrepBrief(m)} disabled={briefingId===m.id}>{briefingId===m.id?<><Spinner size={11}/> Loading</>:" Prep brief"}</Btn>
                <Btn sm danger onClick={()=>cancelMeeting(m.id)}>Cancel</Btn>
              </div>
            </div>
          </Card>
          );
        })}
        {meetings.length===0&&<p style={{color:C.textSub,fontSize:13}}>No meetings yet. Click "+ Book meeting" above.</p>}
      </div>
    </div>
  );
}

//  LINKEDIN 
function LinkedIn({settings,addToast}){
  const [persona,setPersona]=useState("CTO");
  const [hook,setHook]=useState("");
  const [loading,setLoading]=useState(false);
  const [msgs,setMsgs]=useState(null);
  const gen=async()=>{
    setLoading(true);setMsgs(null);
    try{const m=await API.genLinkedIn(persona,hook,settings);setMsgs(m);addToast("Messages generated","success");}
    catch(e){addToast(e.message,"error");}
    setLoading(false);
  };
  const steps=[{n:1,l:"Profile visit + follow",d:"Day 1  warms up prospect"},{n:2,l:"Like / comment on post",d:"Day 23  builds familiarity"},{n:3,l:"Connection request + note",d:"Day 4  personalised per persona"},{n:4,l:"Value-first DM",d:"2448h post-connect"},{n:5,l:"Soft CTA follow-up",d:"Day 710 if no reply"}];
  return(
    <div className="fade-up">
      <SectionHeader title="LinkedIn Outreach" sub="5-step AI workflow  persona-personalised"/>
      <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <Card>
          <p style={{fontSize:13,fontWeight:600,marginBottom:12}}> Workflow steps</p>
          {steps.map((s,i)=>(
            <div key={s.n} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:i<steps.length-1?`1px solid ${C.border}`:"none"}}>
              <div style={{width:24,height:24,borderRadius:"50%",background:C.accentMuted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:C.accent,...mono,flexShrink:0}}>{s.n}</div>
              <div style={{flex:1}}><p style={{fontSize:13,fontWeight:500}}>{s.l}</p><p style={{fontSize:11,color:C.textSub}}>{s.d}</p></div>
              <Toggle on={true} onChange={()=>{}}/>
            </div>
          ))}
        </Card>
        <Card>
          <p style={{fontSize:13,fontWeight:600,marginBottom:12}}> Message generator</p>
          <Label>Persona</Label>
          <select value={persona} onChange={e=>setPersona(e.target.value)} style={{marginBottom:10}}>
            {["CTO","VP Engineering","Director of Engineering","Engineering Manager","Director QA","QA Manager"].map(p=><option key={p}>{p}</option>)}
          </select>
          <Label>Hook / context</Label>
          <input value={hook} onChange={e=>setHook(e.target.value)} placeholder="e.g. saw your post on CI/CD pipelines" style={{marginBottom:12}}/>
          <Btn primary full onClick={gen} disabled={loading}>{loading?<><Spinner size={11}/> Generating</>:" Generate messages"}</Btn>
          {msgs&&(
            <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:10}}>
              {[["Connection note",msgs.note,C.accent],["Follow-up DM",msgs.dm,C.green]].map(([l,v,c])=>(
                <div key={l}><p style={{fontSize:10,color:c,textTransform:"uppercase",letterSpacing:".6px",marginBottom:5}}>{l}</p>
                  <div style={{background:C.bgHover,borderRadius:8,padding:"10px 12px",fontSize:12,lineHeight:1.6,position:"relative"}}>
                    {v}<Btn sm ghost style={{position:"absolute",top:6,right:6}} onClick={()=>navigator.clipboard.writeText(v).then(()=>addToast("Copied","success"))}>Copy</Btn>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

//  PIPELINE
// Real deal tracking (Deal model in db.js) sits downstream of the funnel
// stats above it, which stay computed from Contact/Campaign/Meeting counts
// as before. A deal is the first place a $ value and an explicit win/loss
// outcome get recorded — typically once a prospect reaches a real
// conversation (a meeting booked, or added manually).
const DEAL_STAGE_META=[
  {key:"meeting",label:"Meeting Booked",color:C.purple},
  {key:"proposal",label:"Proposal",color:C.amber},
  {key:"negotiation",label:"Negotiation",color:C.cyan},
  {key:"won",label:"Won",color:C.green},
  {key:"lost",label:"Lost",color:C.red},
];
const emptyDealForm={title:"",value:"",stage:"meeting",contactId:"",notes:""};

function Pipeline({addToast}){
  const [stats,setStats]=useState(null);
  const [deals,setDeals]=useState([]);
  const [summary,setSummary]=useState(null);
  const [contacts,setContacts]=useState([]);
  const [showForm,setShowForm]=useState(false);
  const [editingId,setEditingId]=useState(null);
  const [form,setForm]=useState(emptyDealForm);
  const [saving,setSaving]=useState(false);
  const [dragStage,setDragStage]=useState(null);
  const [confirmDeleteId,setConfirmDeleteId]=useState(null);

  const loadDeals=()=>{
    API.getDeals().then(setDeals).catch(()=>{});
    API.getDealsSummary().then(setSummary).catch(()=>{});
  };
  useEffect(()=>{
    API.getStats().then(setStats).catch(()=>{});
    API.getLeads().then(setContacts).catch(()=>{});
    loadDeals();
  },[]);

  const resetForm=()=>{setForm(emptyDealForm);setEditingId(null);setShowForm(false);};
  const openNew=(stage)=>{setForm({...emptyDealForm,stage:stage||"meeting"});setEditingId(null);setShowForm(true);};
  const openEdit=(d)=>{setForm({title:d.title||"",value:String(d.value||""),stage:d.stage||"meeting",contactId:d.contactId||"",notes:d.notes||""});setEditingId(d.id);setShowForm(true);};

  const saveDeal=async()=>{
    if(!form.title.trim()){addToast("Deal title is required","error");return;}
    setSaving(true);
    try{
      const contact=contacts.find(c=>c.id===form.contactId);
      const payload={title:form.title.trim(),value:Number(form.value)||0,stage:form.stage,contactId:form.contactId||null,contactName:contact?contact.name.trim():"",company:contact?contact.company||"":"",notes:form.notes};
      if(editingId){await API.updateDeal(editingId,payload);addToast("Deal updated","success");}
      else{await API.createDeal(payload);addToast("Deal created","success");}
      resetForm();loadDeals();
    }catch(e){addToast(e.message,"error");}
    setSaving(false);
  };

  const moveDeal=async(id,stage)=>{
    setDeals(p=>p.map(d=>d.id===id?{...d,stage}:d)); // optimistic
    try{await API.updateDeal(id,{stage});loadDeals();}
    catch(e){addToast(e.message,"error");loadDeals();}
  };

  const removeDeal=async(id)=>{
    if(confirmDeleteId!==id){setConfirmDeleteId(id);setTimeout(()=>setConfirmDeleteId(p=>p===id?null:p),3000);return;}
    setConfirmDeleteId(null);
    try{await API.deleteDeal(id);addToast("Deal deleted","success");loadDeals();}
    catch(e){addToast(e.message,"error");}
  };

  const fmtMoney=v=>"$"+Number(v||0).toLocaleString();
  const proposalCount=(summary?.byStage?.proposal?.count||0)+(summary?.byStage?.negotiation?.count||0);
  const wonCount=summary?.byStage?.won?.count||0;
  const stages=[
    {l:"Discovered",n:"94K+",c:C.accent,pct:100},
    {l:"Enriched",  n:stats?.leads||0,  c:C.purple,pct:0.02},
    {l:"Sequenced", n:stats?.sent||0,   c:C.accent,pct:0.35},
    {l:"Opened",    n:stats?.opened||0, c:C.amber, pct:15},
    {l:"Replied",   n:stats?.replied||0,c:C.green, pct:5},
    {l:"Meeting",   n:stats?.meetings||0,c:C.purple,pct:2},
    {l:"Proposal",  n:proposalCount,   c:C.amber, pct:proposalCount?1.5:0},
    {l:"Won",       n:wonCount,        c:C.green, pct:wonCount?1:0},
  ];

  return(
    <div className="fade-up">
      <SectionHeader title="Pipeline" sub="Full funnel from discovery to closed won" right={<Btn sm primary onClick={()=>openNew()}>+ New Deal</Btn>}/>
      <MetricGrid metrics={[
        {label:"Total Enriched",value:String(stats?.leads||0),sub:"database contacts"},
        {label:"Open Pipeline", value:fmtMoney(summary?.openValue),sub:`${summary?.openCount||0} active deal${summary?.openCount===1?"":"s"}`,color:C.accent},
        {label:"Won",value:fmtMoney(summary?.wonValue),sub:`${wonCount} closed won`,color:C.green},
        {label:"Meetings Booked",value:String(stats?.meetings||0),sub:"active",color:C.purple},
      ]}/>
      <Card style={{marginBottom:16}}>
        <p style={{fontSize:13,fontWeight:600,marginBottom:14}}>Funnel stages</p>
        {stages.map(s=>(
          <div key={s.l} style={{display:"flex",alignItems:"center",gap:12,marginBottom:9}}>
            <span style={{fontSize:12,color:C.textSub,minWidth:100}}>{s.l}</span>
            <div style={{flex:1}}><Progress pct={s.pct} color={s.c} h={7}/></div>
            <span style={{fontSize:12,fontWeight:600,...mono,minWidth:50,textAlign:"right",color:s.c}}>{typeof s.n==="number"?s.n.toLocaleString():s.n}</span>
          </div>
        ))}
      </Card>

      {showForm&&(
        <Card style={{marginBottom:16}}>
          <p style={{fontSize:13,fontWeight:600,marginBottom:12}}>{editingId?"Edit deal":"New deal"}</p>
          <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:10,marginBottom:10}}>
            <div><Label>Title</Label><input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="e.g. Acme Corp  annual plan"/></div>
            <div><Label>Value ($)</Label><input type="number" min="0" value={form.value} onChange={e=>setForm(p=>({...p,value:e.target.value}))} placeholder="0"/></div>
            <div><Label>Stage</Label>
              <select value={form.stage} onChange={e=>setForm(p=>({...p,stage:e.target.value}))}>
                {DEAL_STAGE_META.map(s=><option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:10,marginBottom:12}}>
            <div><Label>Contact</Label>
              <select value={form.contactId} onChange={e=>setForm(p=>({...p,contactId:e.target.value}))}>
                <option value="">No linked contact</option>
                {contacts.map(c=><option key={c.id} value={c.id}>{c.name}{c.company?` (${c.company})`:""}</option>)}
              </select>
            </div>
            <div><Label>Notes</Label><input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Optional"/></div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn sm primary disabled={saving} onClick={saveDeal}>{saving?"Saving...":editingId?"Save changes":"Create deal"}</Btn>
            <Btn sm onClick={resetForm}>Cancel</Btn>
          </div>
        </Card>
      )}

      <p style={{fontSize:13,fontWeight:600,marginBottom:10}}>Deals</p>
      <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:`repeat(${DEAL_STAGE_META.length},1fr)`,gap:10}}>
        {DEAL_STAGE_META.map(stageMeta=>{
          const stageDeals=deals.filter(d=>d.stage===stageMeta.key);
          const stageValue=stageDeals.reduce((a,d)=>a+(d.value||0),0);
          return(
            <div key={stageMeta.key}
              onDragOver={e=>{e.preventDefault();setDragStage(stageMeta.key);}}
              onDragLeave={()=>setDragStage(p=>p===stageMeta.key?null:p)}
              onDrop={e=>{e.preventDefault();const id=e.dataTransfer.getData("text/deal-id");if(id)moveDeal(id,stageMeta.key);setDragStage(null);}}
              style={{background:dragStage===stageMeta.key?C.bgHover:C.bgDeep,border:`1px solid ${dragStage===stageMeta.key?stageMeta.color:C.border}`,borderRadius:10,padding:10,minHeight:160,transition:"background .12s,border-color .12s"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <span style={{fontSize:11,fontWeight:600,color:stageMeta.color,textTransform:"uppercase",letterSpacing:".4px"}}>{stageMeta.label}</span>
                <span style={{fontSize:10,color:C.textSub,...mono}}>{stageDeals.length}</span>
              </div>
              {stageValue>0&&<p style={{fontSize:11,color:C.textSub,marginBottom:8,...mono}}>{fmtMoney(stageValue)}</p>}
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {stageDeals.map(d=>(
                  <div key={d.id} draggable
                    onDragStart={e=>e.dataTransfer.setData("text/deal-id",d.id)}
                    onClick={()=>openEdit(d)}
                    style={{background:C.bgCard,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px",cursor:"grab",position:"relative"}}>
                    <p style={{fontSize:12,fontWeight:600,marginBottom:2,paddingRight:16}}>{d.title}</p>
                    {(d.contactName||d.company)&&<p style={{fontSize:11,color:C.textSub,marginBottom:2}}>{d.contactName}{d.contactName&&d.company?" · ":""}{d.company}</p>}
                    {d.value>0&&<p style={{fontSize:11,color:stageMeta.color,...mono,fontWeight:600}}>{fmtMoney(d.value)}</p>}
                    <button onClick={e=>{e.stopPropagation();removeDeal(d.id);}}
                      style={{position:"absolute",top:6,right:6,background:"none",border:"none",cursor:"pointer",color:confirmDeleteId===d.id?C.red:C.textMuted,fontSize:confirmDeleteId===d.id?9:14,fontWeight:confirmDeleteId===d.id?700:400,padding:0,lineHeight:1}}>
                      {confirmDeleteId===d.id?"Confirm?":"×"}
                    </button>
                  </div>
                ))}
                {!stageDeals.length&&<p style={{fontSize:11,color:C.textMuted,textAlign:"center",padding:"14px 0"}}>No deals</p>}
              </div>
              <button onClick={()=>openNew(stageMeta.key)} style={{width:"100%",marginTop:8,background:"none",border:`1px dashed ${C.border}`,borderRadius:6,padding:"5px 0",fontSize:11,color:C.textSub,cursor:"pointer"}}>+ Add</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

//  BILLING 
function Billing({addToast}){
  const [annual,setAnnual]=useState(false);
  const [plans,setPlans]=useState([]);
  const [status,setStatus]=useState(null);
  const [loading,setLoading]=useState(true);
  const [checkingOut,setCheckingOut]=useState(null);

  useEffect(()=>{
    const token=localStorage.getItem("sdr_token");
    Promise.all([
      fetch("/api/billing/plans").then(r=>r.json()),
      fetch("/api/billing/status",{headers:{Authorization:"Bearer "+token}}).then(r=>r.json()),
    ]).then(([p,s])=>{setPlans(Array.isArray(p)?p:[]);setStatus(s);}).catch(()=>{}).finally(()=>setLoading(false));
  },[]);

  const upgrade=async(planId)=>{
    if(planId===status?.plan){addToast("You're already on this plan","info");return;}
    setCheckingOut(planId);
    try{
      const token=localStorage.getItem("sdr_token");
      const r=await fetch("/api/billing/create-checkout-session",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+token},body:JSON.stringify({planId})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      window.location.href=d.url; // real Stripe-hosted checkout page
    }catch(e){addToast(e.message,"error");}
    setCheckingOut(null);
  };

  const openBillingPortal=async()=>{
    try{
      const token=localStorage.getItem("sdr_token");
      const r=await fetch("/api/billing/portal-session",{headers:{Authorization:"Bearer "+token}});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      window.location.href=d.url;
    }catch(e){addToast(e.message,"error");}
  };

  if(loading)return <div className="fade-up" style={{padding:40,textAlign:"center",color:C.textSub}}><Spinner/> Loading billing...</div>;

  return(
    <div className="fade-up">
      <SectionHeader title="Plans & Billing" sub={status?`Currently on ${status.planName} · ${status.seatsUsed}/${status.seatsLimit} seats · ${status.leadsUsed.toLocaleString()}/${status.leadsLimit.toLocaleString()} contacts`:""}/>

      {status?.trialExpired&&(
        <div style={{background:C.redMuted,border:"1px solid "+C.red+"33",borderRadius:10,padding:"12px 16px",marginBottom:16,fontSize:13,color:C.red}}>
          Your free trial has ended. Upgrade below to keep using SDR ReachOut.
        </div>
      )}
      {status?.trialActive&&(
        <div style={{background:C.amberMuted,border:"1px solid "+C.amber+"33",borderRadius:10,padding:"12px 16px",marginBottom:16,fontSize:13,color:C.amber}}>
          Trial ends {new Date(status.trialEnds).toLocaleDateString()}. Upgrade any time before then to avoid interruption.
        </div>
      )}
      {!status?.stripeConfigured&&(
        <div style={{background:C.bgHover,border:"1px solid "+C.border,borderRadius:10,padding:"12px 16px",marginBottom:16,fontSize:12,color:C.textSub}}>
          Payments aren't fully set up yet on this environment — plan pages display correctly, but checkout isn't active until Stripe keys are configured.
        </div>
      )}

      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,marginBottom:24}}>
        <span style={{fontSize:13,color:annual?C.textSub:C.text}}>Monthly</span>
        <Toggle on={annual} onChange={setAnnual}/>
        <span style={{fontSize:13,color:annual?C.text:C.textSub}}>Annual <Badge bg={C.greenMuted} tc={C.green} sm>Save 20%</Badge></span>
      </div>
      <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
        {plans.map(p=>{
          const isCurrent=status?.plan===p.id;
          return(
          <div key={p.id} style={{background:C.bgCard,border:`${isCurrent?"2px":"1px"} solid ${isCurrent?C.accent:p.id==="growth"?C.accent+"44":C.border}`,borderRadius:12,padding:22,position:"relative",transition:"all .15s"}}>
            {p.id==="growth"&&<div style={{position:"absolute",top:-1,left:"50%",transform:"translateX(-50%)",background:C.accent,color:"#fff",fontSize:10,padding:"2px 12px",borderRadius:"0 0 8px 8px",fontWeight:600,letterSpacing:".5px"}}>POPULAR</div>}
            {p.id==="trial"&&<div style={{position:"absolute",top:-1,left:"50%",transform:"translateX(-50%)",background:"#107C10",color:"#fff",fontSize:10,padding:"2px 12px",borderRadius:"0 0 8px 8px",fontWeight:600,letterSpacing:".5px"}}>7 DAYS FREE</div>}
            <p style={{fontSize:14,fontWeight:600,marginBottom:4}}>{p.name}</p>
            <p style={{fontSize:26,fontWeight:700,...mono,color:p.id==="trial"?C.green:C.accent,lineHeight:1,marginBottom:3}}>{p.id==="trial"?"FREE":`$${annual?Math.round(p.price*.8):p.price}`}<span style={{fontSize:13,color:C.textSub,fontWeight:400}}>{p.id==="trial"?" · 7 days":"/mo"}</span></p>
            <p style={{fontSize:11,color:C.textSub,marginBottom:14}}>{p.leads.toLocaleString()} leads · {p.seats} seats</p>
            <Btn primary={isCurrent} full disabled={isCurrent||checkingOut===p.id||p.id==="trial"} onClick={()=>upgrade(p.id)}>
              {isCurrent?"Current plan":checkingOut===p.id?"Redirecting...":p.id==="trial"?"Included at signup":"Upgrade"}
            </Btn>
          </div>
        );})}
      </div>
      {status?.hasPaymentMethod&&(
        <div style={{textAlign:"center"}}>
          <Btn onClick={openBillingPortal}>Manage billing / cancel subscription</Btn>
        </div>
      )}
    </div>
  );
}

//  SETTINGS 
function Settings({outlookConnected,setOutlookConnected,settings,setSettings,addToast,user,isAdmin}){
  const [form,setForm]=useState({tenantId:"",clientId:"",clientSecret:"",senderEmail:settings.senderEmail||user?.email||""});
  const [testing,setTesting]=useState(false);
  const [savingSettings,setSavingSettings]=useState(false);
  const [showDeleteConfirm,setShowDeleteConfirm]=useState(false);
  const [delPassword,setDelPassword]=useState("");
  const [delConfirmText,setDelConfirmText]=useState("");
  const [deleting,setDeleting]=useState(false);
  const closeDeleteModal=()=>{setShowDeleteConfirm(false);setDelPassword("");setDelConfirmText("");};
  const handleDeleteAccount=async()=>{
    if(delConfirmText!=="DELETE"){addToast('Type "DELETE" to confirm','error');return;}
    if(!delPassword){addToast("Enter your password","error");return;}
    setDeleting(true);
    try{
      await API.deleteAccount({password:delPassword,confirmation:delConfirmText});
      localStorage.removeItem("sdr_token");
      localStorage.removeItem("sdr_plan");
      localStorage.removeItem("sdr_onboarding_active");
      window.location.href="/";
    }catch(e){
      addToast(e.message,"error");
      setDeleting(false);
    }
  };
  const testOutlook=async()=>{
    if(!form.tenantId||!form.clientId||!form.clientSecret||!form.senderEmail){addToast("Fill in all 4 Outlook fields","error");return;}
    setTesting(true);
    try{await API.connectOutlook(form);setOutlookConnected(true);addToast("Outlook connected!","success");}
    catch(e){addToast("Connection failed: "+e.message,"error");}
    setTesting(false);
  };
  const disconnectOutlook=async()=>{
    try{await API.disconnectOutlook();setOutlookConnected(false);addToast("Outlook disconnected","info");}
    catch(e){addToast(e.message,"error");}
  };
  const saveSettings=async()=>{
    setSavingSettings(true);
    try{await API.updateSettings(settings);addToast("Settings saved","success");}
    catch(e){addToast(e.message,"error");}
    setSavingSettings(false);
  };
  return(
    <div className="fade-up">
      <SectionHeader title="Settings" sub="Account settings and integrations"/>
      {isAdmin&&(<Card style={{marginBottom:16,border:"1px solid "+(outlookConnected?C.green+"55":C.amber+"55")}}><div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}><p style={{fontSize:13,fontWeight:600}}>Outlook / Microsoft 365</p><Badge bg={outlookConnected?C.greenMuted:C.amberMuted} tc={outlookConnected?C.green:C.amber} dot>{outlookConnected?"Live":"Not connected"}</Badge></div><div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>{[["Azure Tenant ID","tenantId",""],["Azure Client ID","clientId",""],["Azure Client Secret","clientSecret",""],["Sender email (M365 mailbox)","senderEmail",""]].map(([l,k,ph])=>(<div key={k}><Label>{l}</Label><input type={k==="clientSecret"?"password":"text"} value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} placeholder={ph}/></div>))}</div><div style={{display:"flex",gap:8,marginTop:12}}><Btn sm primary onClick={testOutlook} disabled={testing}>{testing?<><Spinner size={11}/> Testing</>:"Test & connect Outlook"}</Btn>{outlookConnected&&<Btn sm danger onClick={disconnectOutlook}>Disconnect</Btn>}</div></Card>)}
      <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
        <Card>
          <p style={{fontSize:13,fontWeight:600,marginBottom:12}}>AI SDR identity</p>
          {[["SDR name","sdrName",""],["Company","company",""],["Value proposition","valueProp",""],["Default sender email","senderEmail",""],["Calendar booking link","calLink",""]].map(([l,k,ph])=>(
            <div key={k} style={{marginBottom:9}}><Label>{l}</Label><input value={settings[k]||""} onChange={e=>setSettings(p=>({...p,[k]:e.target.value}))} placeholder={k==="senderEmail"?user?.email||ph:ph}/></div>
          ))}
          <Btn sm primary onClick={saveSettings} disabled={savingSettings}>{savingSettings?<><Spinner size={11}/> Saving</>:"Save changes"}</Btn>
        </Card>
        <ConnectedAccounts addToast={addToast}/>
      </div>
      <div style={{marginTop:16}}><DomainChecker defaultDomain={settings.senderEmail ? settings.senderEmail.split("@")[1] : ""} /></div>
      <ApiKeysSettings settings={settings} setSettings={setSettings}/>
      <Card style={{marginTop:16,border:"1px solid "+C.red+"30"}}>
        <p style={{fontSize:13,fontWeight:600,marginBottom:4,color:C.red}}>Danger zone</p>
        <p style={{fontSize:12,color:C.textSub,marginBottom:14,maxWidth:560}}>
          Permanently delete your account. If you're the only member of your organization, this deletes your entire
          SDR ReachOut account — contacts, campaigns, sequences, connected mailboxes, everything — and cannot be undone.
          If you have teammates, only your own personal account and data are removed; your organization's shared data stays.
        </p>
        <Btn sm danger onClick={()=>setShowDeleteConfirm(true)}>Delete my account</Btn>
      </Card>
      {/* Migrated onto the shared Modal (ui/Modal.js) — same fields/logic/
          copy as before, now on the C design-system tokens instead of the
          local Fluent T palette, plus a real focus-trap/Escape and
          click-outside-to-close it didn't have previously. */}
      <Modal open={showDeleteConfirm} onClose={()=>{if(!deleting)closeDeleteModal();}} width={380} ariaLabel="Delete account confirmation">
        <ModalBody center={false}>
          <ModalTitle color={C.red}>Delete your account?</ModalTitle>
          <p style={{fontSize:12,color:C.textSub,margin:"6px 0 16px",lineHeight:1.5}}>
            This is permanent and cannot be undone. Confirm your password and type <strong>DELETE</strong> below.
          </p>
          <div style={{marginBottom:10}}>
            <Label>Password</Label>
            <input type="password" value={delPassword} onChange={e=>setDelPassword(e.target.value)} placeholder="Your current password" autoFocus/>
          </div>
          <div style={{marginBottom:18}}>
            <Label>Type DELETE to confirm</Label>
            <input value={delConfirmText} onChange={e=>setDelConfirmText(e.target.value)} placeholder="DELETE"/>
          </div>
          <div style={{display:"flex",gap:10}}>
            <Btn full danger disabled={deleting||delConfirmText!=="DELETE"||!delPassword} onClick={handleDeleteAccount}>
              {deleting?<><Spinner size={11} color={C.red}/> Deleting</>:"Permanently delete"}
            </Btn>
            <Btn full onClick={closeDeleteModal} disabled={deleting}>Cancel</Btn>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

//  PAGE TRANSITION HOST
// Wraps whichever page is currently active (pages[page]) so switching
// pages plays a macOS Dock "Genie" transition: the outgoing page shrinks
// and drifts toward the top-left while fading out, the incoming page
// grows in from a small, slightly tilted state near the top-right. Pure
// wrapper — never touches any individual page's own JSX, state, or data
// fetching, so this can't change what any page does, only how the swap
// between pages looks. Keys off `pageKey`; content itself (`children`) is
// allowed to re-render freely without retriggering the transition.
function PageTransitionHost({ pageKey, children }) {
  const [current, setCurrent] = useState({ key: pageKey, el: children });
  const [outgoing, setOutgoing] = useState(null);
  const prevKeyRef = useRef(pageKey);
  const outTimerRef = useRef(null);
  useLayoutEffect(() => {
    if (pageKey !== prevKeyRef.current) {
      setOutgoing({ key: prevKeyRef.current, el: current.el });
      setCurrent({ key: pageKey, el: children });
      prevKeyRef.current = pageKey;
      if (outTimerRef.current) clearTimeout(outTimerRef.current);
      outTimerRef.current = setTimeout(() => setOutgoing(null), 240);
    } else {
      setCurrent(c => ({ ...c, el: children }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, children]);
  useEffect(() => () => { if (outTimerRef.current) clearTimeout(outTimerRef.current); }, []);
  return (
    <div style={{ position: "relative" }}>
      {outgoing && (
        <div key={outgoing.key + "-out"} className="page-fadescale-out" style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1 }}>
          {outgoing.el}
        </div>
      )}
      <div key={current.key + "-in"} className="page-fadescale-in">
        {current.el}
      </div>
    </div>
  );
}

export default function App(){
 const [authUser,setAuthUser]=useState(null);
  const [authChecked,setAuthChecked]=useState(false);
  // A "Continue with Google/Microsoft/Yahoo" round trip lands back here as
  // ?login_token=... (success) or ?login_error=... (failure) — read those
  // up front so the very first render already knows whether we're about
  // to be logged in, instead of flashing the landing page first.
  const oauthParams=new URLSearchParams(window.location.search);
  const [oauthError]=useState(()=>oauthParams.get("login_error")||"");
  const [showLanding,setShowLanding]=useState(!localStorage.getItem("sdr_token")&&!oauthParams.get("login_token")&&!oauthParams.get("login_error"));
  const [planSelected,setPlanSelected]=useState(()=>!!localStorage.getItem("sdr_plan"));
  const [isMobile,setIsMobile]=useState(()=>window.innerWidth<=768);
  const [mobileMenuOpen,setMobileMenuOpen]=useState(false);
  // Phase 2 (app-shell redesign) — collapsible desktop icon-rail. Persisted
  // so the choice survives a reload; wrapped in try/catch since localStorage
  // can throw in some privacy modes, matching the defensive pattern already
  // used for sdr_token/sdr_plan reads above.
  const [sidebarCollapsed,setSidebarCollapsed]=useState(()=>{
    try{return localStorage.getItem("sdr_sidebar_collapsed")==="1";}catch(e){return false;}
  });
  const toggleSidebarCollapsed=()=>{
    setSidebarCollapsed(p=>{
      const next=!p;
      try{localStorage.setItem("sdr_sidebar_collapsed",next?"1":"0");}catch(e){}
      return next;
    });
  };
  const sidebarWidth=isMobile?0:(sidebarCollapsed?68:210);
  // The desktop sidebar floats with a 12px inset on top/left/bottom
  // (rounded-corner card look) instead of sitting flush against the
  // viewport edges — sidebarOffset is the total horizontal space it
  // occupies from the left edge (inset + its own width) PLUS a visible
  // 16px gap before the main content card starts, so the two floating
  // cards read as distinct panels (not touching) as in the reference
  // layout. Mobile is unaffected (drawer stays flush/full).
  const sidebarOffset=isMobile?0:sidebarWidth+12+16;
  // 7-day free trial signup + onboarding — see TrialSignupPage.js/
  // OnboardingFlow.js. trialSignupOpen shows the dedicated signup page
  // instead of the plain login form; onboardingActive is persisted so the
  // onboarding stepper survives the full-page OAuth redirect that its
  // "Connect Your Workspace" step triggers (see OnboardingFlow's STEP_KEY,
  // which persists which step, separately from whether onboarding is
  // active at all).
  const [trialSignupOpen,setTrialSignupOpen]=useState(false);
  const [onboardingActive,setOnboardingActive]=useState(()=>localStorage.getItem("sdr_onboarding_active")==="1");
  const [trialStatus,setTrialStatus]=useState(null);

  useEffect(()=>{
    const onResize=()=>setIsMobile(window.innerWidth<=768);
    window.addEventListener("resize",onResize);
    return ()=>window.removeEventListener("resize",onResize);
  },[]);

  // A "Continue with Google/Microsoft" login also tries to grab mail
  // send/receive access in that same click (see server.js's
  // linkLoginAccountAsMailbox) — mailboxWarning captures when that part
  // quietly didn't happen (scope declined, or the mailbox is already
  // connected elsewhere) so the toast further down can tell the person
  // their login worked but sending isn't set up yet, instead of leaving
  // them to find out only when a send mysteriously fails later.
  const [mailboxWarning,setMailboxWarning]=useState(null);
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const loginToken=params.get("login_token");
    const warning=params.get("mailbox_warning");
    // Set when this login came from accepting a team invite (see
    // AcceptInvitePage.js and server.js's loginOrSignupOAuthUser) — the
    // person is joining an EXISTING, already-provisioned organization, not
    // starting a fresh one, so they should never see PlanSelectionPage.
    const viaInvite=params.get("via_invite")==="1";
    if(loginToken||params.get("login_error")){
      // One-time-use — pull it out of the URL immediately so it doesn't
      // linger in the address bar or browser history.
      window.history.replaceState({},"",window.location.pathname);
    }
    if(warning)setMailboxWarning(warning);
    if(loginToken)localStorage.setItem("sdr_token",loginToken);
    if(viaInvite)localStorage.setItem("sdr_plan","member");
    const token=loginToken||localStorage.getItem("sdr_token");
    if(token){
      fetch("/api/auth/me",{headers:{Authorization:"Bearer "+token}})
        .then(r=>r.ok?r.json():null)
        .then(user=>{if(user){setAuthUser(user);if(viaInvite){setPlanSelected(true);}else if(user?.role==="Admin"||user?.role==="admin"||user?.permissions?.includes("all")){setPlanSelected(true);localStorage.setItem("sdr_plan","owner");}else if(!localStorage.getItem("sdr_plan")){setPlanSelected(false);}}setAuthChecked(true);})
        .catch(()=>setAuthChecked(true));
    }else{setAuthChecked(true);}
  },[]);

  const handleLogin=(user)=>{ setAuthUser(user); if(user?.role==="Admin"||user?.role==="admin"||user?.permissions?.includes("all")){ setPlanSelected(true); localStorage.setItem("sdr_plan","owner"); }else if(!localStorage.getItem("sdr_plan")){ setPlanSelected(false); } };

  // Trial signup auto-logs the user in (see server.js POST /auth/trial-signup)
  // so the onboarding stepper can start immediately instead of bouncing back
  // through a second login screen.
  const handleTrialSignedUp=(user,token)=>{
    localStorage.setItem("sdr_token",token);
    localStorage.setItem("sdr_plan","trial");
    localStorage.setItem("sdr_onboarding_active","1");
    setAuthUser(user);
    setPlanSelected(true);
    setOnboardingActive(true);
    setTrialSignupOpen(false);
  };
  const handleOnboardingComplete=()=>{
    localStorage.removeItem("sdr_onboarding_active");
    setOnboardingActive(false);
  };

  const [showLogoutConfirm,setShowLogoutConfirm]=useState(false);
  const handleLogout=()=>{
    const token=localStorage.getItem("sdr_token");
    fetch("/api/auth/logout",{method:"POST",headers:{Authorization:"Bearer "+token}}).catch(()=>{});
    localStorage.removeItem("sdr_token");
    localStorage.removeItem("sdr_plan");
    localStorage.removeItem("sdr_onboarding_active");
    localStorage.removeItem("sdr_onboarding_step");
    setAuthUser(null); setShowLanding(true); setOnboardingActive(false); setTrialStatus(null);
  };

  const [page,setPage]=useState(()=>localStorage.getItem("sdr_page")||"dashboard");
 const [toasts,setToasts]=useState([]);useEffect(()=>{localStorage.setItem("sdr_page",page);},[page]);
 const [outlookConnected,setOutlookConnected]=useState(false);
  const [settings,setSettings]=useState({sdrName:"",company:"",valueProp:"",senderEmail:"",calLink:""});

  // Load settings + outlook status from backend on mount
  useEffect(()=>{
    const isAdminUser=authUser?.role==="Admin"||authUser?.role==="admin";
    const defaultSettings={sdrName:(authUser?.firstName||"")+" "+(authUser?.lastName||""),senderEmail:authUser?.email||"",company:"",valueProp:"",calLink:""};
    if(isAdminUser){API.getSettings().then(s=>setSettings(p=>({...defaultSettings,...s}))).catch(()=>{});API.getOutlookStatus().then(s=>setOutlookConnected(s.connected)).catch(()=>{}); }else{setSettings(defaultSettings);API.getUserSettings().then(s=>{if(s&&Object.keys(s).length>0)setSettings(p=>({...defaultSettings,...s}));}).catch(()=>{});API.getUserOutlookStatus().then(s=>{setOutlookConnected(s?.connected||false);}).catch(()=>{}); }
  },[authUser]);
  const addToast=useCallback((msg,type="info",action)=>{const id=Date.now();setToasts(p=>[...p.slice(-4),{id,msg,type,action}]);},[]);
  const removeToast=useCallback((id)=>setToasts(p=>p.filter(t=>t.id!==id)),[]);

  // Surfaces the login-time mailbox_warning captured above, once — and
  // separately, the Settings → Connect with Google/Microsoft round trip
  // (server.js's /oauth/google|microsoft/callback, NOT the login flow)
  // lands back here as ?connected=<provider> or
  // ?connect_error=<provider[_already_connected]>. Previously neither of
  // those was read anywhere on the client, so a successful connect gave no
  // confirmation at all, and a failed one (including the fairly common
  // "already connected to a different account" case) bounced the person
  // back to the app with zero explanation — this is what actually surfaces
  // both as toasts instead of a silently-dropped query param.
  const MAILBOX_WARNING_MESSAGES={
    scope_dropped:"You're logged in, but mail access wasn't granted, so sending isn't set up yet — connect your mailbox from Settings.",
    already_connected:"You're logged in, but that mailbox is already connected to a different SDR ReachOut account — connect a different one from Settings to send.",
    error:"You're logged in, but we couldn't finish connecting your mailbox — try connecting it again from Settings.",
  };
  const CONNECT_ERROR_MESSAGES={
    google_already_connected:"That Google account is already connected to a different SDR ReachOut account — disconnect it there first, or connect a different mailbox.",
    microsoft_already_connected:"That Microsoft account is already connected to a different SDR ReachOut account — disconnect it there first, or connect a different mailbox.",
    google:"Connecting your Google account didn't go through — please try again from Settings.",
    microsoft:"Connecting your Microsoft account didn't go through — please try again from Settings.",
  };
  useEffect(()=>{
    if(mailboxWarning){
      addToast(MAILBOX_WARNING_MESSAGES[mailboxWarning]||"You're logged in, but your mailbox isn't fully connected — check Settings.","info");
      setMailboxWarning(null);
    }
  },[mailboxWarning,addToast]);
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const connected=params.get("connected");
    const connectErr=params.get("connect_error");
    if(!connected&&!connectErr)return;
    window.history.replaceState({},"",window.location.pathname);
    if(connected)addToast((connected==="google"?"Google":"Microsoft")+" account connected — you can now send from it.","success");
    else addToast(CONNECT_ERROR_MESSAGES[connectErr]||"Connecting your mailbox didn't go through — please try again from Settings.","error");
  },[addToast]);

  // The verify-email link (server.js GET /auth/verify-email) redirects back
  // here as ?verified=1 or ?verify_error=<code> — this is usually opened in
  // a DIFFERENT tab than the one running onboarding, so it's just a toast;
  // OnboardingFlow itself polls trial-status independently to notice
  // verification from whichever tab is actually showing the stepper.
  const VERIFY_ERROR_MESSAGES={
    expired_token:"That verification link expired — request a new one from the onboarding screen.",
    invalid_token:"That verification link isn't valid — request a new one from the onboarding screen.",
    missing_token:"That verification link looks incomplete — request a new one from the onboarding screen.",
    server_error:"We couldn't verify that email right now — please try again.",
  };
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const verified=params.get("verified");
    const verifyErr=params.get("verify_error");
    if(!verified&&!verifyErr)return;
    window.history.replaceState({},"",window.location.pathname);
    if(verified)addToast("Email verified — you're all set.","success");
    else addToast(VERIFY_ERROR_MESSAGES[verifyErr]||"We couldn't verify that email — please try again.","error");
  },[addToast]);

  // Trial status badge (Sidebar) — fetched whenever we have a logged-in
  // user, and again right after onboarding finishes so the badge reflects
  // the real trialEnds date instead of staying blank until the next
  // natural refresh.
  useEffect(()=>{
    if(!authUser)return;
    API.getTrialStatus().then(setTrialStatus).catch(()=>{});
  },[authUser,onboardingActive]);

  const shared={outlookConnected,setOutlookConnected,settings,setSettings,addToast};
  const userPlan = localStorage.getItem("sdr_plan") || "trial";
  const isOwner = authUser?.role==="Admin"||authUser?.role==="admin"||authUser?.permissions?.includes("all");
  const planFeatures = {
    trial:   ["dashboard","contacts","campaigns","sequences","templates","replies","billing","settings"],
    starter: ["dashboard","contacts","campaigns","sequences","templates","replies","billing","settings"],
    growth:  ["dashboard","contacts","campaigns","sequences","templates","linkedin","replies","meetings","calendar","pipeline","billing","settings"],
    scale:   ["dashboard","contacts","campaigns","sequences","templates","linkedin","replies","meetings","calendar","pipeline","team","workspace","mailmerge","billing","settings"],
    owner:   ["dashboard","contacts","campaigns","sequences","templates","linkedin","replies","meetings","calendar","pipeline","team","workspace","mailmerge","billing","settings","discover"],
  };
  const allowed = planFeatures.owner;
  const LockedPage = ({name}) => (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60vh",gap:16}}>
      <div style={{fontSize:48}}>&#128274;</div>
      <h2 style={{fontSize:20,fontWeight:700,color:C.text}}>Upgrade to access {name}</h2>
      <p style={{fontSize:14,color:C.textSub}}>This feature is not available on your current plan.</p>
      <button onClick={()=>setPage("billing")} style={{background:"linear-gradient(135deg,#0078D4,#7B2FBE)",color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",fontSize:14,fontWeight:600,cursor:"pointer"}}>Upgrade Plan</button>
    </div>
  );
  const isAdmin=authUser?.role==="Admin"||authUser?.role==="admin"||authUser?.permissions?.includes("all");
  const NewUserEmpty=({icon,title,sub,btn,onClick})=>(<div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"60vh",gap:16,textAlign:"center",padding:"40px"}}><div style={{fontSize:56}}>{icon}</div><h2 style={{fontSize:22,fontWeight:700,color:C.text,marginTop:8}}>{title}</h2><p style={{fontSize:14,color:C.textSub,maxWidth:400,lineHeight:1.6}}>{sub}</p>{btn&&<button onClick={onClick} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",fontSize:14,fontWeight:600,cursor:"pointer",marginTop:8}}>{btn}</button>}</div>);
  const guard = (id, el) => allowed.includes(id) ? el : (isAdmin ? <LockedPage name={id}/> : <NewUserEmpty icon="" title="Feature Locked" sub="This feature will be available once your account is set up. Contact your admin to get access." />);
  const pages={
    dashboard:<Dashboard setActive={setPage} outlookConnected={outlookConnected} user={authUser}/>,
    workspace:guard("workspace",<OutlookWorkspace settings={settings} addToast={addToast} onLogout={()=>setShowLogoutConfirm(true)} onOpenSettings={()=>setPage("settings")}/>),
    campaigns:guard("campaigns",<CampaignsPage addToast={addToast}/>),
    contacts:guard("contacts",<ContactsPage addToast={addToast} settings={settings} setSettings={setSettings}/>),
    discover:guard("discover",<Discover addToast={addToast} settings={settings}/>),
    sequences:guard("sequences",<SequenceBuilder settings={settings} addToast={addToast}/>),
    templates:guard("templates",<TemplatesPage addToast={addToast}/>),
    linkedin:guard("linkedin",<LinkedIn settings={settings} addToast={addToast}/>),
    replies:guard("replies",<Replies {...shared}/>),
    meetings:guard("meetings",<Meetings {...shared} setActive={setPage}/>),
    calendar:guard("calendar",<CalendarPage settings={settings} addToast={addToast}/>),
    pipeline:guard("pipeline",<Pipeline addToast={addToast}/>),
    team:guard("team",<TeamPage addToast={addToast}/>),
    warmup:<EmailWarmup addToast={addToast}/>,
    billing:<Billing addToast={addToast}/>,
    settings:<Settings {...shared} user={authUser} isAdmin={isAdmin}/>,
  };

  return(
    window.location.pathname==="/terms"?<TermsOfService/>:
    window.location.pathname==="/privacy"?<PrivacyPolicy/>:
    window.location.pathname.startsWith("/invite/")?<AcceptInvitePage/>:
    <TimezoneProvider>
    <>
      <style>{css}</style>
      {/* Page canvas is now a soft light-grey backdrop (was plain white) so
          the floating, rounded-corner sidebar and main content panel both
          read as distinct white cards against it — matching the reference
          layout. Purely a backdrop color; every page's own content still
          renders inside the white <main> card exactly as before. */}
      <div style={{display:"flex",minHeight:"100vh",background:C.bgAlt}}>
        <Sidebar active={page} setActive={setPage} connected={outlookConnected} onLogout={()=>setShowLogoutConfirm(true)} user={authUser} isMobile={isMobile} mobileOpen={mobileMenuOpen} onCloseMobile={()=>setMobileMenuOpen(false)} trialStatus={trialStatus} collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebarCollapsed}/>
        {isMobile&&authUser&&planSelected&&(
          <button className="mobile-hamburger" onClick={()=>setMobileMenuOpen(true)} aria-label="Open menu" style={{position:"fixed",top:14,left:14,zIndex:90,width:42,height:42,borderRadius:10,background:"#fff",border:`1px solid ${C.borderSoft}`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 6px rgba(16,24,40,.08), 0 1px 2px rgba(16,24,40,.06)"}}>
            <span style={{display:"flex",flexDirection:"column",gap:4}}>
              <span style={{width:18,height:2,background:C.accent,borderRadius:1}}/>
              <span style={{width:18,height:2,background:C.accent,borderRadius:1,opacity:.7}}/>
              <span style={{width:18,height:2,background:C.accent,borderRadius:1,opacity:.45}}/>
            </span>
          </button>
        )}
        <main className="app-main" style={{
          marginLeft:sidebarOffset,
          marginTop:isMobile?0:12,marginBottom:isMobile?0:12,
          background:C.bg,
          border:isMobile?"none":`1px solid ${C.borderSoft}`,
          borderRadius:isMobile?0:18,
          boxShadow:isMobile?"none":"0 1px 2px rgba(16,24,40,.04), 0 12px 28px rgba(16,24,40,.06)",
          transition:"margin-left .18s cubic-bezier(.32,.72,0,1)",flex:1,
          padding:page==="calendar"?"0":(isMobile?"64px 16px 20px":"28px 32px"),
          maxWidth:isMobile?"100vw":`calc(100vw - ${sidebarOffset}px - 12px)`,
          minHeight:isMobile?"100vh":"calc(100vh - 24px)",
          overflowX:"hidden",
          height:isMobile?"100vh":"calc(100vh - 24px)",
          overflow:page==="calendar"?"hidden":"auto",
        }}>
          {!authChecked?(
  <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",flexDirection:"column",gap:12}}>
    <div style={{width:36,height:36,border:"3px solid #1A1E2E",borderTopColor:"#4F6EF7",borderRadius:"50%",animation:"spin .7s linear infinite"}}/>
    <p style={{color:"#5A6180",fontSize:13}}>Loading...</p>
  </div>
):!authUser&&trialSignupOpen?(
  <div style={{position:"fixed",inset:0,zIndex:9999,overflowY:"auto"}}><TrialSignupPage onSignedUp={handleTrialSignedUp} onBack={()=>setTrialSignupOpen(false)} onLogin={()=>{setTrialSignupOpen(false);setShowLanding(false);}}/></div>
):!authUser&&showLanding?(
  <div style={{position:"fixed",inset:0,zIndex:9999,overflowY:"auto"}}><LandingPage onGetStarted={()=>setTrialSignupOpen(true)} onLogin={()=>setShowLanding(false)}/></div>
):!authUser?(
  <div style={{position:"fixed",inset:0,zIndex:9999,overflowY:"auto"}}><LoginPage onLogin={handleLogin} initialError={oauthError}/></div>
):onboardingActive?(
  <div style={{position:"fixed",inset:0,zIndex:9998,overflowY:"auto"}}><OnboardingFlow user={authUser} addToast={addToast} onComplete={handleOnboardingComplete}/></div>
):!planSelected?(
  <div style={{position:"fixed",inset:0,zIndex:9998,overflowY:"auto"}}><PlanSelectionPage onSelect={(plan)=>{localStorage.setItem("sdr_plan",plan);setPlanSelected(true);}} user={authUser}/></div>
):(
  window.location.pathname==="/workspace"?<div style={{position:"fixed",inset:0,zIndex:99999,overflow:"hidden"}}><OutlookWorkspace settings={settings} addToast={addToast} onLogout={()=>setShowLogoutConfirm(true)} onOpenSettings={()=>{setPage("settings");window.location.href="/";}}/></div>:
  window.location.pathname==="/mailmerge"?<div style={{position:"fixed",inset:0,zIndex:99999,overflow:"hidden"}}><MailMerge settings={settings} addToast={addToast}/></div>:
  <PageTransitionHost pageKey={page}>{pages[page]||<Dashboard setActive={setPage} outlookConnected={outlookConnected} user={authUser}/>}</PageTransitionHost>
)}
        </main>
      </div>
      {/* z-index here has to clear 100000, not just Toast's own 9999 — the
          standalone /workspace and /mailmerge routes above render inside
          their own position:fixed,zIndex:99999 full-screen wrapper, which
          otherwise paints its opaque background right over every toast
          (success/error/info, not just this one) whenever either of those
          routes is open. Confirmed pre-existing: toasts on those two routes
          have been invisible before this change too, not a regression. */}
      <div style={{position:"fixed",bottom:24,right:24,display:"flex",flexDirection:"column",gap:8,zIndex:100000}}>
        {toasts.map(t=><Toast key={t.id} msg={t.msg} type={t.type} action={t.action} onClose={()=>removeToast(t.id)}/>)}
      </div>
    </>
    {/* Native iOS/macOS-style alert — white card, hairline-divided text
        buttons (system blue Cancel / bold red destructive action), no
        gradient or dark chrome, matching Apple's system alert convention
        rather than the app's own branded dialog style. Now built on the
        shared Modal (ui/Modal.js) — same exact markup/behavior as before,
        plus a real focus-trap/Escape (via useModalA11y) it didn't have
        previously. */}
    <Modal open={showLogoutConfirm} onClose={()=>setShowLogoutConfirm(false)} width={270} ariaLabel="Log out confirmation">
      <ModalBody>
        <ModalTitle>Log Out?</ModalTitle>
        <ModalDescription>You'll be redirected to the login page.</ModalDescription>
      </ModalBody>
      <ModalActions>
        <ModalAction onClick={()=>setShowLogoutConfirm(false)}>Cancel</ModalAction>
        <ModalAction destructive onClick={()=>{setShowLogoutConfirm(false);handleLogout();}}>Log Out</ModalAction>
      </ModalActions>
    </Modal>
    </TimezoneProvider>
  );
}